/**
 * THE API CONTRACT THE UI CODES AGAINST.
 *
 * The API is not running yet, so this file is the written contract, not a guess made per screen.
 * Every type here mirrors something the server already owns:
 *
 *   DenyReason / AuthzStage / DenyBody  ←  packages/contracts/src/decision.ts
 *   SessionContext                      ←  packages/contracts/src/vocabulary.ts
 *   CompanyLifecycleStatus              ←  packages/contracts/src/enums.ts
 *   ModuleCode + status                 ←  packages/contracts/src/modules.ts
 *
 * They are re-declared rather than imported because `@glpoint/contracts` publishes from `dist/` and
 * this app must build with Vite from source alone. `src/api/contract.mirror.test.ts` reads the
 * contracts source files off disk and fails if this mirror ever drifts — the mirror is checked, not
 * trusted.
 */

// ── decision.ts ───────────────────────────────────────────────────────────────────────────────────

export const AUTHZ_STAGES = [
  'ROUTE',
  'AUTHN',
  'TENANT',
  'SUBSCRIPTION',
  'COMPANY',
  'MODULE',
  'CAPABILITY',
  'CSRF',
  'RATE',
] as const;
export type AuthzStage = (typeof AUTHZ_STAGES)[number];

export const DENY_REASONS = [
  'declaration_missing',
  'no_session',
  'session_expired',
  'session_revoked',
  'anonymous_not_permitted',
  'session_context_mismatch',
  'stepup_required',
  'no_tenant_context',
  'wrong_context',
  'platform_context_no_grant',
  'cross_tenant',
  'subscription_inactive',
  'subscription_trialing_restricted',
  'subscription_past_due',
  'subscription_suspended',
  'subscription_expired',
  'no_company_grant',
  'company_not_resolvable',
  'company_suspended',
  'company_archived',
  'company_review_required',
  'company_inactive',
  'not_subscribed',
  'sku_expired',
  'unknown_module',
  'missing_module',
  'company_not_assigned',
  'limit_reached',
  'missing_capability',
  'capability_wrong_context',
  'csrf_failed',
  'origin_not_allowed',
  'rate_limited',
  'predicate_error',
  'indeterminate',
] as const;
export type DenyReason = (typeof DENY_REASONS)[number];

/**
 * The body a denial produces (decision.ts `toDenyBody`).
 *
 * CF-37's two-layer contract, and it is the reason the UI has one denial component and not a `403`
 * page: outside the tenant `reason` and `stage` are ABSENT and the UI must say nothing more than the
 * uniform sentence. Inside the tenant the first failing stage's reason arrives and the UI owes the
 * user a written next step (09 §5.8).
 */
export interface DenyBody {
  readonly error: string;
  readonly reason?: DenyReason;
  readonly stage?: AuthzStage;
  readonly requestUid: string;
}

// ── vocabulary.ts ─────────────────────────────────────────────────────────────────────────────────

/**
 * 09 §"two named surfaces" / 04 I6: exactly one context is active per session, and it is a property
 * of the SESSION — never of the user, never of the request. PLATFORM and TENANT never bleed and are
 * never simultaneous.
 */
export const SESSION_CONTEXTS = ['PLATFORM', 'TENANT'] as const;
export type SessionContext = (typeof SESSION_CONTEXTS)[number];

// ── enums.ts ──────────────────────────────────────────────────────────────────────────────────────

/** 09 §5.1 / decision 10: a company's lifecycle state is DISPLAYED, never inferred. */
export const COMPANY_LIFECYCLE_STATUSES = [
  'prospect',
  'active',
  'dormant',
  'former_client',
  'merged_into',
  'quarantined',
  'source_unavailable',
  'review_required',
] as const;
export type CompanyLifecycleStatus = (typeof COMPANY_LIFECYCLE_STATUSES)[number];

export const CATALOGUE_STATUSES = ['active', 'planned', 'retired'] as const;
export type CatalogueStatus = (typeof CATALOGUE_STATUSES)[number];

// ── Resources the shell reads ─────────────────────────────────────────────────────────────────────

/**
 * The signed-in session as the server describes it.
 *
 * There is no client-side permission logic anywhere in this app. `capabilities` is here for UX
 * affordances ONLY — hiding a control the server would deny anyway. A hidden button is an
 * affordance, never a control: the gate decides, and it decides again on every request.
 */
export interface SessionSummary {
  readonly userUid: string;
  readonly displayName: string;
  readonly sessionContext: SessionContext;
  /** 09 §3.1: the ארגון slot is shown only when the user belongs to more than one tenant. */
  readonly tenants: readonly TenantSummary[];
  readonly activeTenantUid: string | null;
  readonly capabilities: readonly string[];
  /** Non-null ⇒ a PLATFORM principal is inside a tenant under a support grant (04 §13.1.5). */
  readonly supportGrantUid: string | null;
  /**
   * TRUE ⇒ the server is refusing every tenant-scoped route until this person enrols a second
   * factor. **2026-10-25.**
   *
   * It is NOT a permission and nothing in this app may branch on it to show or hide a control. It is
   * the server telling the browser what the server has already decided, so the shell can put the
   * enrolment screen in front of the person instead of a workspace in which every panel fails with
   * the same denial and nothing explains it.
   */
  readonly mfaEnrolmentRequired: boolean;
  /**
   * WHERE A PERSON REACHES A PERSON, OR `null`. **2026-11-03.**
   *
   * The estate's configured reply-to, carried here because
   * `mail-identity-is-configuration` forbids an e-mail address literal anywhere under
   * `apps/web/src` — and it is right: an address in a component is an address nobody can change
   * without a release.
   *
   * `null` MEANS NO ADDRESS IS CONFIGURED, and the assistant says so rather than rendering a
   * `mailto:` that goes nowhere. It is never substituted with the sending address, which the
   * server's own `MailIdentity` calls *"routinely unmonitored"*.
   */
  readonly supportContactAddress: string | null;
}

export interface TenantSummary {
  readonly tenantUid: string;
  readonly displayName: string;
}

/**
 * One row of the single server-side "visible companies" answer (09 §5.1, CONFLICT C-1).
 * The picker never shows a company the user may not open and then denies on click.
 */
export interface CompanySummary {
  readonly companyUid: string;
  /** 09 §9.1 V1: the full legal name. Never a db code, never a FIN cid, never a unit slug. */
  readonly legalName: string;
  /**
   * The name the office gave the company, which is the one a person recognises. It can differ from
   * the legal name and the server has always sent it; this contract did not declare it, so every
   * screen fell back to `legalName`.
   */
  readonly displayName: string;
  /** 09 §5.1: ח.פ as a muted second line. Nullable — a broken-identity row still appears. */
  readonly registrationNumber: string | null;
  /**
   * `core.company.entity_kind` — what the company IS. NOT NULL in the schema and set at creation.
   *
   * Declared here 2026-10-25. The settings panel reported it as *"stored but not read"* and the
   * edit form started blank with its save button disabled, because this contract did not name a
   * field the server was already sending.
   */
  readonly entityKind: string;
  /** `confirmed` · `unverified` · `not_available_at_source` · `disputed` (0002's own constraint). */
  readonly taxIdStatus: string;
  readonly lifecycleStatus: CompanyLifecycleStatus;
  /** 09 §5.1 "broken identity": the row appears, marked, rather than silently vanishing. */
  readonly needsAttention: boolean;
}

/**
 * Module entitlement as the server resolved it — Subscription → SKU → sku_module → module_code.
 * The UI never resolves entitlement; it renders the answer.
 */
export interface ModuleEntitlement {
  readonly moduleCode: string;
  readonly displayNameHe: string;
  readonly catalogueStatus: CatalogueStatus;
  /** `true` only when the tenant bought it AND the server says this company is in scope. */
  readonly available: boolean;
  /** Present when `available` is false; drives the Hebrew explanation (09 §5.8). */
  readonly unavailableReason?: DenyReason;
}

/** 09 §5.2: the period picker expresses a period TYPE, not just a value (CONFLICT C-2). */
export type PeriodKind = 'year' | 'half' | 'quarter' | 'bimonthly' | 'month' | 'as_of';

export interface PeriodSummary {
  readonly periodUid: string;
  readonly kind: PeriodKind;
  /** Already resolved to business form by the server. 09 §5.2: never the raw source key. */
  readonly displayHe: string;
  /** 09 §5.2: a locked period is selectable and readable, and shows a lock chip. */
  readonly locked: boolean;
}

/**
 * One blocker on נייר השלמות (09 §5.7b). A blocked document is a checklist, not an error.
 * B2 is binding: every blocker carries a deep link to the exact place the value is entered.
 */
export interface DocumentBlocker {
  readonly blockerUid: string;
  /** In business Hebrew — "שיעור ריבית להלוואות לזמן ארוך", never a column name. */
  readonly whatIsMissingHe: string;
  /** B2: the deep link. A blocker with no link is a support call. */
  readonly whereEnteredHref: string;
  readonly whereEnteredLabelHe: string;
  readonly ownerDisplayName: string | null;
}

export interface BlockedDocument {
  readonly documentUid: string;
  readonly documentKindHe: string;
  readonly companyLegalName: string;
  readonly periodDisplayHe: string;
  readonly blockers: readonly DocumentBlocker[];
  /** B4: the draft is producible even when the final is blocked. */
  readonly draftAvailable: boolean;
}

// ══════════════════════════════════════════════════════════════════════════════════════════════════
// ADMINISTRATION VOCABULARY
//
// The Control Plane and the Tenant Admin screens speak the vocabulary the server already owns:
//
//   TENANT_STATUSES / SUBSCRIPTION_STATES / MEMBERSHIP_STATUSES / ACCESS_LEVELS
//                                        ←  packages/contracts/src/enums.ts
//   SEEDED_TENANT_ROLES / PLATFORM_ROLES ←  packages/contracts/src/capabilities.ts
//
// Same rule as above: re-declared because this app builds from source with Vite, and
// `contract.mirror.test.ts` reads the authoritative files off disk so the copy is checked.
// ══════════════════════════════════════════════════════════════════════════════════════════════════

/**
 * ONE ENQUIRY, AS THE OFFICE THAT RAISED IT SEES IT.
 *
 * NO `handlerDisplayName` AND NO `releaseIdentity`. Which of our people holds a ticket and which
 * build the customer was on are OUR facts: the first is an internal assignment and the second a
 * diagnostic. The platform's own board has both; this shape has no field for either, so there is
 * nothing for a later edit to forget to strip.
 */
export interface OfficeEnquiry {
  readonly ticketUid: string;
  /** `SUP-10042` — the number in the acknowledgement mail, which is how somebody finds this row. */
  readonly displayNo: string;
  readonly openedByDisplayName: string | null;
  readonly subject: string;
  readonly body: string;
  /** `open` | `assigned` | `escalated` | `answered` | `closed`. */
  readonly state: string;
  readonly openedAt: string;
  readonly respondByAt: string;
  readonly firstResponseAt: string | null;
  readonly closedAt: string | null;
}

/**
 * ONE MESSAGE IN AN ENQUIRY'S THREAD.
 *
 * `authorSide` IS THE SERVER'S, written from the authorisation context when the words were
 * saved — never from a request body, which is what stops an office writing an answer in our
 * name. The screen reads it to decide which side of the thread a message is drawn on.
 */
export interface OfficeEnquiryMessage {
  readonly messageUid: string;
  /** `office` | `platform`. An unknown value is drawn neutrally rather than as one of the two. */
  readonly authorSide: string;
  readonly authorDisplayName: string | null;
  readonly body: string;
  readonly createdAt: string;
}

export interface OfficeEnquiryThread {
  readonly messages: readonly OfficeEnquiryMessage[];
  /**
   * WHETHER THIS ENQUIRY STILL TAKES WORDS. Decided by the server from the enquiry's state, so a
   * screen that drew a reply box on a closed enquiry would be contradicted by the first press
   * rather than by nothing.
   */
  readonly mayReply: boolean;
}

export interface OfficeEnquiriesRead {
  /**
   * WHICH LIST THIS IS, AND IT IS NOT DECORATION.
   *
   * `office` — the reader owns this office and is seeing every enquiry it raised.
   * `own`    — everybody else, seeing the ones they opened.
   *
   * A list that silently held fewer rows for one person than for another is a list somebody
   * reads as *"my colleague's enquiry was lost"*. The screen says which one it is drawing.
   */
  readonly scope: 'office' | 'own';
  readonly enquiries: readonly OfficeEnquiry[];
}

export const TENANT_STATUSES = ['active', 'trial', 'suspended', 'closed'] as const;
export type TenantStatus = (typeof TENANT_STATUSES)[number];

export const SUBSCRIPTION_STATES = [
  'draft',
  'trialing',
  'active',
  'past_due',
  'suspended',
  'cancelled',
  'expired',
] as const;
export type SubscriptionState = (typeof SUBSCRIPTION_STATES)[number];

/** 01 §A3 — a status per MEMBERSHIP, never on the identity. */
export const MEMBERSHIP_STATUSES = ['invited', 'active', 'suspended', 'disabled'] as const;
export type MembershipStatus = (typeof MEMBERSHIP_STATUSES)[number];

/** 01 §A5 — read / write / approve. There is deliberately NO `deny`: exclusion is the absent row. */
export const ACCESS_LEVELS = ['read', 'write', 'approve'] as const;

/*
 * ══ THE LEVELS A PERSON MAY CHOOSE — TWO, NOT THREE. **2026-10-28.** ═══════════════════════════
 *
 * Owner: *"ושוב רמת הגישה — אני לא מבין מה זה צפייה, עדכון, אישור. דיברנו על זה."* We had, and his
 * instruction then was: *"הצג דרך לפשט את הבחירה תוך שמירה על נתונים ותאימות, ובלי להרחיב
 * הרשאות."*
 *
 * THE FACT, read out of ACCESS_LEVEL_PERMITS in the contracts package and not out of anybody's
 * memory — the table the gate consults at stage 4:
 *
 *     read     read: true   write: false   export: true   deliver: false
 *     write    read: true   write: true    export: true   deliver: true
 *     approve  read: true   write: true    export: true   deliver: true
 *
 * The last two rows are IDENTICAL. The gate reads that table and nothing else, so the two choices
 * have had the same effect since the table was written: two buttons that look different and behave
 * the same, which is what he is objecting to.
 *
 * SO THE CHOICE IS TWO, AND NOTHING ELSE MOVES. The vocabulary above is untouched, the database
 * CHECK is untouched, the gate is untouched, and a row already stored as the third value keeps it
 * and keeps working. What changes is what a person is OFFERED, and what a stored third value is
 * CALLED — it is shown as עריכה, which is exactly what it does.
 *
 * NO PERMISSION IS WIDENED. The product stops granting the third value and grants the second; the
 * two permit the same four action classes. If a rule ever makes approval mean something, this one
 * line is what re-opens the third choice.
 */
export const OFFERED_ACCESS_LEVELS = ['read', 'write'] as const;
export type AccessLevel = (typeof ACCESS_LEVELS)[number];

/** The four classes an action can be, as the gate classifies a route's effects. */
export const ACTION_CLASSES = ['read', 'write', 'export', 'deliver'] as const;
export type ActionClass = (typeof ACTION_CLASSES)[number];

/**
 * WHAT EACH ACCESS LEVEL PERMITS — mirrored from `packages/contracts/src/enums.ts`, which is what
 * `gate.ts` stage 4(d) reads before it admits a request to a company.
 *
 * **Section 5, 2026-10-28.** The Owner asked what `אישור` allows in practice. The table is the
 * answer, and it is mirrored here so the screen can SHOW the answer rather than a sentence
 * somebody wrote: `approve` and `write` carry identical permissions today.
 *
 * `contract.mirror.test.ts` compares this to the authoritative table cell by cell, so it cannot
 * drift — and the day an approve-only action class is declared, the screen's wording follows.
 */
export const ACCESS_LEVEL_PERMITS: Readonly<
  Record<AccessLevel, Readonly<Record<ActionClass, boolean>>>
> = {
  read: { read: true, write: false, export: true, deliver: false },
  write: { read: true, write: true, export: true, deliver: true },
  approve: { read: true, write: true, export: true, deliver: true },
};

export const SEEDED_TENANT_ROLES = [
  'tenant_owner',
  'tenant_admin',
  'tenant_member',
  'tenant_billing_contact',
  'client_viewer',
] as const;
export type SeededTenantRole = (typeof SEEDED_TENANT_ROLES)[number];

export const PLATFORM_ROLES = [
  'platform_owner',
  'platform_operator',
  'platform_support',
  'platform_auditor',
] as const;
export type PlatformRole = (typeof PLATFORM_ROLES)[number];

/** One row of the Control Plane's tenant list. Every count here was computed by the server. */
export interface TenantAdminSummary {
  readonly tenantUid: string;
  /** 09 §9.1 V1: the office's own legal name. Never a slug as the identity. */
  readonly legalName: string;
  /** The opaque handle it was provisioned under. A muted second line, never the identity. */
  readonly slug: string;
  readonly status: TenantStatus;
  /**
   * `null` WHEN THE CUSTOMER HAS NO LIVE SUBSCRIPTION, which is an ordinary state and not an error.
   *
   * This was declared non-null and the server did not send the field at all, so every screen that
   * rendered it read `undefined`. `contract.mirror.test.ts` now binds this interface to the
   * service that answers `platform.tenant.read`, so a field declared here and absent there fails
   * the build instead of the page.
   */
  readonly subscriptionState: SubscriptionState | null;
  readonly companyCount: number;
  readonly seatCount: number;
  /**
   * THE CUSTOMER NUMBER. `GLP-10001` onward. Migration `0020`, Owner finding 7:
   * *"נדרש מספר לקוח שהמערכת מקצה ומציגה בכרטיס וברשימות, בנפרד מח״פ ומה־UID הטכני."*
   *
   * All three of those now exist and are different things: `tenantUid` is the machine handle,
   * `slug` is the provisioning handle that 02 §5.1 calls *"human-readable, NOT a key"*, a company's
   * ח״פ belongs to its legal entity, and THIS is the number a person reads out on the telephone.
   *
   * `null` for a customer created before numbering -- rendered *"טרם הוקצה"*, never blank.
   */
  readonly displayNo: string | null;
}

/** `apps/api/src/services.ts` MembershipView, in the shape a Hebrew screen needs. */
export interface MembershipSummary {
  readonly membershipUid: string;
  /**
   * The ACCOUNT behind this membership. Declared 2026-10-26.
   *
   * The server has always sent it and this contract did not name it, so the browser held a list of
   * the office's people and no way to say which one was signed in -- which is why the grant screen
   * could not open on *you*. It is an identifier for matching and never an authorization input.
   */
  readonly userUid: string;
  readonly displayName: string;
  /** Shown LTR inside an RTL row (09 §9.5 A1: bidi is controlled, never inferred). */
  readonly email: string;
  readonly status: MembershipStatus;
  /**
   * OA-0001 §3.7: an invitee arrives with NO capabilities, so this list is legitimately empty, and
   * an empty list is a STATE the screen writes out — never a blank cell.
   */
  readonly roleCodes: readonly string[];
  /** 01 §A3: `invited` and `active` consume a seat. Stated by the server, never derived here. */
  readonly consumesSeat: boolean;
}

/** `core.role` + `core.role_permission`, as the roles screen reads them. */
export interface RoleSummary {
  readonly roleCode: string;
  readonly displayNameHe: string;
  /** Capability codes. `capabilityDescriptionHe` turns each one into office Hebrew. */
  readonly capabilities: readonly string[];
  /** A role a tenant created for one person. Shown, because it is the one that drifts. */
  readonly isPersonal: boolean;
}

/**
 * THE FOUR ANSWERS A ROLE ASSIGNMENT CAN BE REFUSED WITH — OA-0001 §3.3.
 *
 * Mirrored from `apps/api/src` rather than from `packages/contracts`, because that is where they are
 * declared: the first two are `CeilingDecision.reason` in `domain/capability-ceiling.ts`, the last
 * two are the store outcomes of `assignRoles` in `services/customer-admin.ts`. The mirror is checked
 * against those files off disk, exactly as the contracts mirror is — a copy nobody verifies is how
 * two namespaces get invented (G-06).
 *
 * They are NOT deny reasons. The gate had already allowed the request when they were produced: the
 * caller holds `tenant.role.manage` and is inside their tenant. What these say is that the ROW may
 * not be written, which is a rule about a FUTURE request and not an access decision on this one —
 * the distinction `capability-ceiling.ts` opens with, and the reason they travel as a conflict and
 * never as a `DenyBody`.
 */
export const ROLE_ASSIGNMENT_REFUSALS = [
  'exceeds_granter_capabilities',
  'delegation_capability_not_delegatable',
  'unknown_role',
  'membership_not_found',
] as const;
export type RoleAssignmentRefusalReason = (typeof ROLE_ASSIGNMENT_REFUSALS)[number];

/**
 * The refusal as the SERVER produced it.
 *
 * `excess` is the list `checkDelegationCeiling` returns instead of a boolean, and it is the whole
 * point of the shape: *"you may not grant these three capabilities" is actionable in a way that
 * `403 forbidden` is not*. It carries capability CODES; `roleAssignmentText.ts` turns them into the
 * catalogue's Hebrew, and no screen ever renders one.
 *
 * Empty for `unknown_role` and `membership_not_found`, which are not about capabilities at all.
 */
export interface RoleAssignmentRefusal {
  readonly reason: RoleAssignmentRefusalReason;
  readonly excess: readonly string[];
}

/** One row of `core.user_company_access`. CG-2: there is no `deny` level and no `deny` row. */
export interface CompanyGrantSummary {
  readonly membershipUid: string;
  readonly companyUid: string;
  readonly accessLevel: AccessLevel;
  readonly grantedAtUtc: string;
  readonly expiresAtUtc: string | null;
}


/* ── THE BILLING CONSOLE AND THE STATEMENT. **2026-11-02.** ────────────────────────────

   MIRRORED FROM THE PRODUCER AND CHECKED AGAINST IT. `contract.mirror.test.ts` reads
   `apps/api/src/services/billing-run.ts` and `apps/api/src/services/billing-statement.ts` off disk
   and holds these field for field — the same control X9 and X10 put on the support-grant and audit
   shapes, and the one the subscription shape was missing when it crashed on 2026-11-02.

   THE AUTHORITATIVE DECLARATION IS THE SERVICE'S, not `@glpoint/contracts`. Those two shapes have
   exactly one producer each and are not restated anywhere on the server; what was missing was a
   check that the BROWSER agreed, and that is what the mirror cases are. */

/** One priced article, as the operator is shown it before approving the charge. */
export interface RunCalculationLine {
  readonly skuCode: string;
  readonly displayNameHe: string;
  readonly quantity: number;
  readonly chargedDays: number;
  readonly periodDays: number;
  readonly grossAmount: string;
  readonly benefitAmount: string;
  readonly amount: string;
  readonly benefitKind: string | null;
}

/**
 * WHAT HAPPENED — OR WOULD HAPPEN — TO ONE SUBSCRIPTION.
 *
 * `outcome` is a member of a closed vocabulary the server owns; it is NOT narrowed to a union
 * here, because a word this file did not know would then fail to parse rather than render as the
 * unfamiliar outcome it is. `billingOutcomeHe` maps the ones we have words for and names the rest.
 */
/** A component considered and charged nothing, with the reason. See `RunLine.included`. */
export interface RunWaivedLine {
  readonly skuCode: string;
  readonly displayNameHe: string;
  readonly quantity: number;
  readonly includedQuantity: number;
  /** `included_in_plan` · `fully_waived` · `not_held`. */
  readonly reason: string;
  readonly benefitKind: string | null;
  /** What it WOULD have cost before the benefit. `0.00` when the plan simply includes it. */
  readonly grossAmount: string;
}

export interface RunLine {
  readonly subscriptionUid: string;
  readonly tenantUid: string;
  readonly tenantLegalName: string;
  /**
   * ── THE COLUMNS THE OWNER'S RECOMMENDATION TABLE OPENS WITH. **2026-11-03.** ───────────────
   *
   *     *"מספר לקוח, שם לקוח, חבילה, מודולים שנרכשו, חברות / סביבות, משתמשים, תקופת חיוב,
   *      לפני מע״מ, מע״מ, סכום לחיוב, מצב"*
   *
   * The counts are the ones the FIGURE was priced on — the largest quantity measured in the
   * period, per dimension — so the number beside the money explains the money. A count read
   * separately could disagree with it.
   */
  readonly tenantDisplayNo: string | null;
  readonly planNameHe: string;
  readonly moduleCount: number | null;
  readonly companyCount: number | null;
  readonly userCount: number | null;
  readonly periodStart: string;
  readonly periodEnd: string;
  /*
   * ── THE TRIAL, ON THE ROW IT EXPLAINS. **2026-11-04.** ─────────────────────────────
   *
   * **The Owner:** *"הצגת תקופת הניסיון ומועד סיומה בשורת החיוב."*
   *
   * It was ALREADY READ and already decisive: `priceCycle` takes it, `skipped_trial` is an
   * outcome because of it, and a trial that ends mid-period is why a line's charged days are
   * fewer than the period's. Every one of those is a figure on this row that the row could not
   * explain, because the date that produces them stopped at the service boundary.
   *
   * `null` for a subscription that never had a trial, and the screen prints nothing — not a
   * dash standing for a date, which would read as a trial whose end is unknown.
   */
  readonly trialEndsAt: string | null;
  readonly outcome: string;
  readonly amountTotal: string | null;
  readonly documentUid: string | null;
  readonly calculation: readonly RunCalculationLine[];
  /**
   * THE COMPONENTS THAT COST NOTHING, AND WHY.
   *
   *     *"הצג גם רכיבים שנכללו בחבילה או ניתנו בהטבה של 100%, עם סכום 0 ₪ והסיבה."*
   *
   * `calculation` is the DOCUMENT's and carries no zero line. This is the operator's half.
   */
  readonly included: readonly RunWaivedLine[];
  readonly amountExVat: string | null;
  readonly vatRatePct: string | null;
  readonly vatAmount: string | null;
  readonly detail: string | null;
  readonly inferredDays: number;
}

/** A dated plan term the pass found due. Reported apart from the lines, and for that reason. */
export interface PlanMoveLine {
  readonly subscriptionUid: string;
  readonly outcome: string;
  readonly detail: string | null;
}

export interface RunReport {
  readonly asOf: string;
  readonly considered: number;
  readonly inferredDays: number;
  readonly planMoves: readonly PlanMoveLine[];
  readonly tenants: number;
  readonly lines: readonly RunLine[];
  readonly tally: Readonly<Record<string, number>>;
}

/** One document as the statement carries it — the recorded figures, with nothing derived. */
export interface StatementDocument {
  readonly documentUid: string;
  readonly documentKind: string;
  /** `null` until the provider issues one. A number we invented would be a document we invented. */
  readonly documentNumber: string | null;
  readonly periodStart: string | null;
  readonly periodEnd: string | null;
  readonly amountExVat: string;
  readonly vatRatePct: string;
  readonly vatAmount: string;
  readonly amountTotal: string;
  readonly currencyCode: string;
  /** `pending_issue` | `issued` | `provider_failed` | `void`. */
  readonly status: string;
  readonly issuedAt: string | null;
  readonly providerFailureReason: string | null;
  readonly createdAt: string;
  readonly providerDocumentType: string | null;
  /** `118.00 ₪`, formatted by the server. A client that joined the code on would be a second
      place the symbol is decided — which is what printed `1287.00 ILS` on the operator's card. */
  readonly amountTotalDisplay: string | null;
}

/**
 * WHEN THE NEXT ATTEMPT FALLS. A shape and not a nullable date, for the reason the capacity limit
 * is a shape: `null` would make "nothing is scheduled" and "we do not know" the same answer.
 */
export type NextCharge =
  | { readonly scheduled: true; readonly on: string }
  | { readonly scheduled: false; readonly reason: 'state_not_billable' | 'no_subscription' };

export interface PaymentStanding {
  readonly stage: 'current' | 'warning' | 'restricted' | 'terminated';
  readonly attempts: number;
  readonly unsettledSince: string | null;
  /** `false` ⇒ no dunning policy is in force, so `current` means "nothing said otherwise". */
  readonly governed: boolean;
}

/**
 * ONE SUPPORT TICKET, AS THE BOARD READS IT. 0055.
 *
 * The Owner: *"הלוח יציג מספר פנייה, מצב, מטפל, מועד פתיחה, יעד תגובה והפרות SLA."*
 *
 * `displayNo` IS WHAT A PERSON SAYS OUT LOUD and `ticketUid` is what a URL carries. Both travel,
 * because a board that showed a uid would be unusable on a telephone and one that showed only the
 * number could not address a row.
 *
 * `firstResponseAt` AND NOT `closedAt` IS WHAT THE SLA IS READ AGAINST. A ticket somebody replied
 * to and left open is on time; one closed without a word is not. Folding them would make "closed
 * quickly" look like "answered quickly", which is the number somebody might be tempted to improve.
 */
export interface SupportTicket {
  readonly ticketUid: string;
  readonly displayNo: string;
  readonly tenantUid: string;
  readonly tenantDisplayName: string;
  readonly openedByDisplayName: string | null;
  readonly subject: string;
  readonly body: string;
  readonly screenId: string | null;
  readonly releaseIdentity: string | null;
  /** `open` | `assigned` | `escalated` | `answered` | `closed`. */
  readonly state: string;
  readonly handlerDisplayName: string | null;
  readonly openedAt: string;
  readonly respondByAt: string;
  readonly firstResponseAt: string | null;
  readonly closedAt: string | null;
}

export interface BillingStatement {
  /**
   * THE OFFICE'S OWN SUBSCRIPTION, or `null` when it has none.
   *
   * The settle press needs no body — the server reads the subscription from the caller's bound
   * context — so this field is not an address the client sends anywhere. It is what tells the
   * screen whether there is anything to settle at all: `null` is a real, ordinary state for an
   * office provisioned without a subscription, and the act is hidden rather than offered and
   * refused.
   */
  readonly subscriptionUid: string | null;
  readonly documents: readonly StatementDocument[];
  /** `null` ⇒ טרם בוצע חיוב — no row exists, which is ordinary inside a first period. */
  readonly lastDocument: StatementDocument | null;
  readonly nextCharge: NextCharge;
  readonly payment: PaymentStanding;
}

/**
 * ONE LINE OF THE ESTIMATE. The SAME shape the invoice's lines have, because it is the same
 * function that produced it — `priceCycle` over a period that has not happened yet.
 */
export interface EstimateLine {
  readonly skuCode: string;
  readonly displayNameHe: string;
  readonly quantity: number;
  readonly chargedDays: number;
  readonly periodDays: number;
  readonly grossAmount: string;
  readonly benefitAmount: string;
  readonly amount: string;
  readonly benefitKind: string | null;
  /** `100.00 ₪`, formatted by the server. `null` on a line with no benefit, and never `0.00 ₪`. */
  readonly grossAmountDisplay: string | null;
  readonly benefitAmountDisplay: string | null;
  readonly amountDisplay: string | null;
}

/**
 * WHAT THE NEXT PERIOD WOULD COST, IF NOTHING CHANGES — item 3, 2026-11-03.
 *
 * A SHAPE AND NOT A NULLABLE AMOUNT, for the reason `NextCharge` above is a shape: `null` would
 * make *"this office will not be charged"*, *"the estate stopped measuring"* and *"a SKU has no
 * price"* the same answer, and two of those three are somebody's to act on.
 *
 * `assumedFrom` IS THE ASSUMPTION, IN THE PAYLOAD. The figure is the quantities measured on that
 * day, held for every day of the next period. A client that received the total without the day it
 * was projected from could only render it as a price — which is the one thing this must not be
 * read as.
 */
export type NextChargeEstimate =
  | {
      readonly available: true;
      readonly periodStart: string;
      readonly periodEnd: string;
      readonly assumedFrom: string;
      readonly lines: readonly EstimateLine[];
      readonly amountExVat: string;
      readonly vatRatePct: string;
      readonly vatAmount: string;
      readonly amountTotal: string;
      /** The three figures a person reads. The raw fields above are for comparison, not display. */
      readonly amountExVatDisplay: string | null;
      readonly vatAmountDisplay: string | null;
      readonly amountTotalDisplay: string | null;
      readonly currencyCode: string;
    }
  | {
      readonly available: false;
      /** The server's own closed list. Rendered by name; an unknown one is said to be unknown. */
      readonly reason: string;
      readonly detail: string | null;
    };

// ── The payment-failure path (0038/0039) — the arrears list and the day counts ────────────

/**
 * ONE OFFICE IN ARREARS, WITH THE STAGE ALREADY DECIDED.
 *
 * `stage`, `daysUnsettled` and `demands` are COMPUTED BY THE SERVER using the same two functions
 * the nightly pass acts on. This screen must not compute them: a table that received the day count
 * and compared it against thresholds of its own would be a second copy of the policy, and the copy
 * is always the one that drifts — it would show `warning` beside an office the pass had already
 * restricted.
 */
export interface ArrearsLine {
  readonly tenantUid: string;
  readonly legalName: string;
  readonly firstFailedOn: string;
  readonly attemptCount: number;
  readonly lastAttemptOn: string;
  readonly lastFailureCode: string | null;
  readonly hasDocument: boolean;
  readonly stage: DunningStage;
  readonly daysUnsettled: number | null;
  /** What the stage demands of the subscription, or `null` when it demands nothing. */
  readonly demands: string | null;
}

export type DunningStage = 'current' | 'warning' | 'restricted' | 'terminated';

/**
 * 0047. ONE COMMERCIAL CHANGE REQUEST — what an office asked, and what it was told.
 *
 * `requestKind` and `status` are STRINGS and not unions, deliberately: both are
 * `CK_ccr_kind`/`CK_ccr_status` vocabularies the server refuses outside of, and a union here
 * would be a second copy free to drift from the constraint. The screens render an unrecognised
 * value as its own code rather than defaulting it to a member.
 *
 * NO AMOUNT, EITHER DIRECTION. 08 §8.4 — see the panel's header.
 */
export interface ChangeRequestRead {
  readonly changeRequestUid: string;
  readonly tenantUid: string;
  readonly tenantLegalName: string;
  readonly requestKind: string;
  readonly detail: string;
  readonly requestedByDisplayName: string;
  readonly status: string;
  /** `null` while open. `CK_ccr_resolved_has_answer` guarantees a closed row carries words. */
  readonly resolution: string | null;
  readonly resolvedByDisplayName: string | null;
  readonly resolvedAtUtc: string | null;
  readonly createdAtUtc: string;
}

/** The day counts in force from a given date. An INSERT, never an edit — see the set operation. */
export interface DunningPolicyRead {
  readonly effectiveFrom: string;
  readonly warnAfterDays: number;
  readonly restrictAfterDays: number;
  readonly terminateAfterDays: number;
  readonly noticeIntervalDays: number;
}

export interface ArrearsReport {
  readonly on: string;
  /**
   * `null` ⇒ NO DATED POLICY GOVERNS TODAY, and that is not a default to paper over: in that state
   * the pass refuses every office with `no_governing_policy` and every line reads `current`,
   * because nothing is in force. A screen that showed thresholds anyway would be showing numbers
   * nobody chose.
   */
  readonly policy: DunningPolicyRead | null;
  readonly lines: readonly ArrearsLine[];
}

export interface DunningPolicyReport {
  readonly on: string;
  readonly governing: DunningPolicyRead | null;
  readonly policies: readonly DunningPolicyRead[];
}

/** What one review did — three fields, because they fail independently. */
export interface DunningReviewRead {
  readonly stage: DunningStage;
  readonly daysUnsettled: number | null;
  /** `null` ⇒ the state already matched what the stage demands, or nothing is demanded. */
  readonly stateChangedTo: string | null;
  readonly notice: { readonly kind: string; readonly stage?: string; readonly reason?: string };
  readonly refusal: string | null;
}

// ── Commercial capacity (OA-0001 §3.11 / 08 §7) ───────────────────────────────────────────────────

export type CapacityKind = 'company' | 'seat' | 'module_company';

/**
 * UNMETERED IS A SHAPE, NOT A NULL — mirrored deliberately from `apps/api/src/domain/capacity.ts`.
 * There, the union exists because `row?.limit ?? null` is the one-character way to turn a failed
 * read into "unlimited". The same union on this side means a screen cannot render "ללא הגבלה" for a
 * number it simply failed to receive.
 */
export type CapacityLimit =
  | { readonly metered: false }
  | { readonly metered: true; readonly limit: number };

export interface CapacityUsage {
  readonly kind: CapacityKind;
  /** Rows that already exist and count against the limit, as the server counted them. */
  readonly current: number;
  readonly limit: CapacityLimit;
}

/**
 * The refusal the SERVER produced when the (N+1)th could not be created — 08 §7's "block CREATING
 * the next, never disable what already works", carrying the count, the limit and where to go.
 *
 * The UI renders this. It never computes one: a screen that decided for itself that a customer is
 * at their limit would be a second implementation of a commercial rule, and the first one is in
 * `apps/api/src/domain/capacity.ts`.
 */
export interface CapacityRefusal {
  readonly reason: 'limit_reached';
  readonly kind: CapacityKind;
  readonly current: number;
  readonly limit: number;
  /** An address inside this product, built by `routes.ts`. Never an external URL. */
  readonly upgradeHref: string;
}

/**
 * MIRRORS `TenantSubscriptionSummary` in `@glpoint/contracts`, field for field, checked by
 * `contract.mirror.test.ts`.
 *
 * **2026-11-02, the Owner's estate.** This shape and `SubscriptionView` in
 * `apps/api/src/services.ts` were declared independently and agreed on ONE field of six. Measured
 * off the live server, `GET /api/tenant/subscription` answered `planCode`, `periodStart`,
 * `periodEnd`, `periodKind`, `trialEndsAt`; this declared `planNameHe`, `periodStartUtc`,
 * `periodEndUtc`, `trialEndsAtUtc` and `capacity`. The plan line was blank, three dates were
 * `undefined` through a formatter that does not refuse one, and `מנוי וחשבוניות`
 * reached the render-fault boundary on `capacity.length`.
 *
 * `capacity` IS GONE FROM HERE, and not because it was in the way: usage is a live count and has
 * its own route, `tenant.capacity.read`, exactly as the control plane has always read it. Its own
 * read is what gives it its own loading state, its own not-found and its own failure — three
 * sentences one optional array cannot say.
 */
export interface SubscriptionSummary {
  readonly subscriptionUid: string;
  readonly state: SubscriptionState;
  readonly planNameHe: string;
  readonly periodStartUtc: string;
  readonly periodEndUtc: string;
  readonly trialEndsAtUtc: string | null;
  /** Module codes bought. Never a SKU — 08 D12: the gate never sees one. */
  readonly licensedModules: readonly string[];
}

/**
 * ONE CUSTOMER'S SUBSCRIPTION AS THE CONTROL PLANE SEES IT.
 *
 * Deliberately NOT `SubscriptionSummary` above, which is what an OFFICE sees about itself: that one
 * carries a Hebrew plan NAME and capacity usage, both of which are computed for a tenant reading its
 * own file. This is the platform's view of the same row -- the plan CODE it was sold, the state, the
 * period and the modules -- and the two are different questions that happen to touch one table.
 *
 * It carries no amount, no price and no discount: 08 §8.4 / CF-66 keep money out of entitlement, and
 * the capability this is read under names metadata only.
 */
export interface PlatformSubscriptionSummary {
  readonly subscriptionUid: string;
  readonly state: SubscriptionState;
  /** The catalogue code, not a display name. The platform sells codes. */
  readonly planCode: string;
  readonly periodStart: string;
  readonly periodEnd: string;
  readonly periodKind: string;
  readonly trialEndsAt: string | null;
  readonly licensedModules: readonly string[];
}

/**
 * ONE AUDIT EVENT AS THE CONTROL PLANE SEES IT.
 *
 * DELIBERATELY NOT `AuditEventSummary`, which is the OFFICE's view. Both now carry the stable
 * `action` CODE — X10 removed the office view's `actionHe`, which no producer ever sent — and the
 * two remain different questions over one table: the office's view carries an actor display NAME and
 * no `routeId`, `entityType` or `capability`, because it is read by the customer whose data was
 * touched; this is the raw administrative record read by the operator of the platform, and it NAMES
 * THE CUSTOMER, which the office's view deliberately does not: there is only one tenant an office's
 * trail could be about, and a cross-tenant read is unreadable without one.
 *
 * NOT ONE FIELD OF THIS SHAPE CHANGED in X10. `occurredAt` keeps its spelling even though the
 * office's view says `occurredAtUtc`: renaming it would alter a second route's response body for no
 * reason this ticket carries.
 */
export interface PlatformAuditEventSummary {
  readonly occurredAt: string;
  /** `null` for a platform-context event, which belongs to no customer. */
  readonly tenantUid: string | null;
  readonly actorKind: string;
  readonly actorUserUid: string | null;
  readonly action: string;
  readonly entityType: string;
  readonly outcome: string;
  readonly denyReason: string | null;
  readonly routeId: string | null;
  /** Non-null ⇒ the action happened under a support grant, and that is never hidden. */
  readonly supportGrantUid: string | null;
}

/**
 * ONE PRICE BOOK — 08 §11 entity #8.
 *
 * A named, dated, single-currency set of prices. One book is PUBLISHED (the list price a new
 * customer is sold at); the rest are negotiated books pinned to one subscription, which is Owner
 * Decision D4's mechanism for a grandfathered agreement with no schema exception.
 */
export interface PriceBookSummary {
  readonly priceBookUid: string;
  readonly code: string;
  readonly displayNameHe: string;
  /** ISO 4217. A book holds one currency: a price list in two currencies is two lists. */
  readonly currencyCode: string;
  readonly isPublished: boolean;
  readonly status: string;
  readonly itemCount: number;
}

/**
 * One dated, banded price.
 *
 * `unitPrice` is a STRING and stays one: money is fixed-point (§12A.2), and an amount that passed
 * through a JS number has been rounded before anybody read it.
 */
export interface PriceItemSummary {
  /**
   * Whether the SALE would use this row today, decided by the server's effective-price resolver.
   * F1R-02.
   *
   * NOT derivable from `effectiveTo` on this side: an older open-ended row succeeded by a newer one
   * is still open-ended, and the screen used to call both of them current.
   */
  readonly isCurrent: boolean;
  readonly skuCode: string;
  /** `null` ⇒ this price applies on every plan. */
  readonly planCode: string | null;
  /**
   * `volume` | `graduated` — WHICH UNITS THIS BAND'S RATE APPLIES TO. 0040.
   *
   * A band shown as `1–50 · 10.00` beside `51+ · 8.00` is two different prices for one office of
   * sixty — 480 or 580 — and the columns alone could not say which. It is a property of the SKU,
   * so every row of one SKU carries the same value.
   */
  readonly tierMode: string;
  readonly tierFrom: number;
  readonly tierTo: number | null;
  readonly unitPrice: string;
  readonly effectiveFrom: string;
  /** Non-null ⇒ superseded. The row stays, because it still prices the period it priced. */
  readonly effectiveTo: string | null;
}

/**
 * THE OWNER DASHBOARD'S MEASURED FIGURES.
 *
 * Every one of these was reported as NEEDS_BACKEND for two rounds: the data existed and nothing
 * exposed it. They are measured in ONE statement so they cannot disagree with each other.
 *
 * `mrr`, `arr` and `churnedMrr` are decimal STRINGS and stay strings: §12A.2 makes money
 * fixed-point, and a figure that passed through a JS number was rounded before it was rendered.
 */
export interface PlatformMetricsSummary {
  readonly newCustomers: number;
  readonly trialConversions: number;
  readonly churnedCustomers: number;
  /**
   * The sum of commercial price snapshots on live lines, GROSS and UN-NORMALISED. F1R-05.
   *
   * Not MRR. `billing.subscription_discount` is not applied and `subscription.period_kind` is not
   * normalised, so a 100% waived customer is counted at list price and an annual subscription is
   * added to monthly ones. Useful as an integrity signal; not a revenue figure.
   */
  readonly contractedSnapshotTotal: string;
  readonly churnedSnapshotTotal: string;
  /** How many active subscriptions carry a live discount the total above ignores. */
  readonly discountedSubscriptions: number;
  /** Whether a completed billing engine stands behind those figures. `false` until Billing Core. */
  readonly recurringRevenueAvailable: false;
  readonly activeModules: number;
  /**
   * Live lines on active subscriptions carrying NO price. FND-09.
   *
   * Zero means the revenue figures beside it are complete. Anything else means they measure part of
   * the book, and the screen must say so rather than present a subtotal as a total.
   */
  readonly unpricedLines: number;
  /** The start of the window the flow measures cover, so the screen can state it. */
  readonly sinceUtc: string;
}

/**
 * One plan in the product catalogue. Seeded by 0003 and, until now, read by nothing — which is why
 * assigning a plan meant typing its code from memory.
 *
 * `assignable` is the SERVER's derivation. A screen that decided for itself which statuses may be
 * sold would be a second commercial rule, and the first one is in `services/catalogue.ts`.
 */
export interface PlanCatalogueEntry {
  readonly planCode: string;
  readonly displayNameHe: string;
  readonly displayNameEn: string;
  readonly status: string;
  readonly assignable: boolean;
  /**
   * THE SECOND LOCK ON THE PUBLIC DOOR — may a stranger buy this plan. 0032, §10ב.
   *
   * ADDITIVE on a shape clients already read (MC-2). It arrives `false` on every plan, and until
   * `PlatformSignupDoorScreen` there was nothing in the product that could show its state: only the
   * signup store read the column, as a filter. A lock nobody can see is a lock somebody opens twice.
   *
   * It is NOT the same question as `assignable`: an operator may assign a plan to a customer they
   * are provisioning by hand whether or not a stranger may buy it, and the two locks on the public
   * door are `self_signup_open` plus this.
   */
  readonly publicSignup: boolean;
  /**
   * How many SKUs the plan includes. ADDITIVE on a shape clients already read (MC-2), and a real `0`.
   *
   * A plan with no SKU includes nothing, so the figure is what separates "this plan is empty" from
   * "the row did not render".
   *
   * THIS SAID *"plan → SKU → module is the path the entitlement resolver walks"*, and it does not:
   * measured on 2026-10-20, `entitlement-store.ts` walks `subscription_sku → sku_module → module`
   * and reads `billing.plan_sku` nowhere. The plan's composition reaches a customer as entitlement
   * LINES on their subscription — `platform.subscription.plan.apply` writes them — and this count
   * is a catalogue fact about the plan, never a statement about what any customer holds.
   */
  readonly skuCount: number;
  /**
   * HOW MANY LIVE SUBSCRIPTIONS THIS PLAN PRICES. **The impact preview, 2026-10-20.**
   *
   * The commercial chapter forbids changing a package's composition in a way that moves existing
   * rights silently, and requires the effect shown before the change. An operator including a
   * product in a plan reads this number first. It does not mean those customers gain anything:
   * a composition reaches an existing subscription only when it is applied to it explicitly.
   */
  readonly liveSubscriptionCount: number;
  /**
   * THE SYSTEM-ISSUED NUMBER. `PLN-10001` / `PRD-10001`. Migration `0020`, Owner finding 7:
   * *"נדרש מספר לקוח שהמערכת מקצה ומציגה בכרטיס וברשימות"*, and the same for a plan and a product.
   *
   * SEPARATE FROM THE CODE, which is unchanged and is still what contracts reference:
   * *"אין להחליף קודים עסקיים או מזהים קיימים בשקט לצורך הוספת מספר תצוגה"*. `null` means the row
   * pre-dates numbering and is rendered *"טרם הוקצה"*, never as a blank cell.
   *
   * It does NOT sort lexicographically once the digit count changes, because nothing pads --
   * `GLP-100000` sorts before `GLP-99999`. No list sorts by it; `displayNumberSequence` exists for
   * anything that must.
   */
  readonly displayNo: string | null;
  /**
   * WHAT THE PLAN INCLUDES, member by member. **Added 2026-10-21, Owner requirement.**
   *
   * *"ודא שאפשר גם לערוך הרכב קיים ולהוציא רכיב מחבילה, ולא רק ליצור ולהוסיף."* `skuCount` above
   * says HOW MANY and never WHICH, so the catalogue screen had no composition to render and
   * therefore nothing to edit. This is that list.
   *
   * `liveHolders` is how many live subscriptions hold THIS member right now -- the impact the edit
   * must acknowledge. `null` is NOT READ, never `0`: a screen told zero would offer a removal as
   * harmless, and the write refuses `impact_unknown` in that state, so the two halves agree.
   */
  readonly includedSkus: readonly {
    readonly skuCode: string;
    readonly displayNameHe: string;
    readonly includedQuantity: number;
    readonly skuStatus: string;
    readonly liveHolders: number | null;
  }[];
}

/** One seeded `core.module` code, as the SKU→module attach offers it. X3. */
export interface ModuleCatalogueEntry {
  readonly moduleCode: string;
  readonly displayNameHe: string;
  readonly status: string;
}

/** One seeded `billing.charge_dimension` code, as the SKU create offers it. X3. */
export interface ChargeDimensionEntry {
  readonly dimensionCode: string;
  /**
   * THE SEEDED HEBREW, carried rather than reinvented. **2026-10-20.**
   *
   * `billing.charge_dimension` has held `display_name_he` since 0005 -- *"לפי חברה"*, *"לפי ארגון"*,
   * *"לפי משתמש"* -- and the console was rendering `dimensionCode` to a Hebrew reader because this
   * read did not carry it. A map in the client would be a second, drifting copy of a catalogue
   * column, which is what the Owner's instruction rules out.
   */
  readonly displayNameHe: string;
  readonly grain: string;
}

/** One SKU, with the module codes it licenses. 08 D12: a SKU is commercial, a module is access. */
export interface SkuCatalogueEntry {
  readonly skuCode: string;
  readonly displayNameHe: string;
  readonly displayNameEn: string;
  readonly chargeDimensionCode: string;
  readonly status: string;
  readonly sellable: boolean;
  readonly moduleCodes: readonly string[];
  /**
   * THE SYSTEM-ISSUED NUMBER. `PLN-10001` / `PRD-10001`. Migration `0020`, Owner finding 7:
   * *"נדרש מספר לקוח שהמערכת מקצה ומציגה בכרטיס וברשימות"*, and the same for a plan and a product.
   *
   * SEPARATE FROM THE CODE, which is unchanged and is still what contracts reference:
   * *"אין להחליף קודים עסקיים או מזהים קיימים בשקט לצורך הוספת מספר תצוגה"*. `null` means the row
   * pre-dates numbering and is rendered *"טרם הוקצה"*, never as a blank cell.
   *
   * It does NOT sort lexicographically once the digit count changes, because nothing pads --
   * `GLP-100000` sorts before `GLP-99999`. No list sorts by it; `displayNumberSequence` exists for
   * anything that must.
   */
  readonly displayNo: string | null;
}

/** One reason the gate refused something, with how often and how recently. */
export interface DenialSummary {
  readonly denyReason: string;
  readonly count: number;
  readonly lastOccurredAtUtc: string;
}

/** One recorded run of a scheduled job. 04 §10.2 / G-17. */
export interface JobRunSummary {
  readonly jobId: string;
  readonly startedAtUtc: string;
  readonly finishedAtUtc: string | null;
  readonly outcome: string | null;
  readonly tenantsProcessed: number | null;
  readonly assertedArtifact: boolean;
  readonly errorText: string | null;
}

/**
 * The platform's own condition. `unmeasured` carries the CODES of subsystems that write no rows in
 * this build; the screen turns each into a sentence. Rendering a zero for them instead would say
 * nothing is wrong with something nothing can see.
 */
/** The two audit failures R-8 names. Additive-only, like every other code a client reads. */
export const INCIDENT_KINDS = ['audit_unavailable', 'audit_chain_fork'] as const;
export type IncidentKind = (typeof INCIDENT_KINDS)[number];

/**
 * One recorded audit incident — R-8's evidence.
 *
 * Four fields, and the server will never send a fifth: no payload, no actor, no token. `requestUid`
 * is the reference the refused caller was shown, which is how a support call joins to this row.
 */
export interface IncidentSummary {
  readonly kind: IncidentKind;
  readonly requestUid: string;
  readonly routeId: string | null;
  readonly occurredAtUtc: string;
}

/**
 * WHAT THE ESTATE SAYS ABOUT RELEASES, for About System.
 *
 * `available` is `null` whenever there is nothing to offer, and `reason` says WHICH nothing it is:
 * an estate with no registry declared, one whose registry cannot be read, one with nothing
 * published, one that is current, and one that cannot name its own release all render as the same
 * blank space on screen. They are not the same fact, and an operator debugging the second needs to
 * be able to tell it from the fourth.
 */
/**
 * One deployment of a release ON THIS ESTATE, including the ones that failed.
 *
 * §9.1(9) asks for failure to be reported honestly, and §9.1(6) for About to show the VERIFIED
 * installed identity — so `verifiedTag` is what the running process answered after the deployment,
 * not what the deployment intended. It is null unless the state is `verified`.
 */
export interface DeploymentRecordView {
  readonly deploymentUid: string;
  readonly artifactSha256: string;
  readonly releaseTag: string;
  readonly releaseCommit: string;
  readonly state: 'requested' | 'running' | 'verified' | 'failed';
  readonly requestedAtUtc: string;
  readonly startedAtUtc: string | null;
  readonly finishedAtUtc: string | null;
  readonly verifiedTag: string | null;
  readonly verifiedCommit: string | null;
  readonly detail: string | null;
}

/** What Platform Release Management shows: the candidate, the history, and whether it is accepted. */
export interface ReleaseOverview {
  readonly availability: ReleaseAvailability;
  readonly deployments: readonly DeploymentRecordView[];
  readonly acceptedHere: boolean;
  /**
   * WHAT CONFIRMING THIS UPDATE WOULD DO TO THE DATABASE. **Owner decision, 2026-10-19.**
   *
   * `stated: false` means the published entry does not say -- an entry from before the migration
   * identity was carried -- and the server then reports `schemaChange: true`, because an unknown
   * read as "no change" is how an application-only path accepts incompatible contents.
   */
  readonly candidateSchema: {
    readonly schemaChange: boolean;
    readonly stated: boolean;
    readonly pending: number | null;
    readonly appliedHere: number;
  };
}export interface ReleaseReview {
  readonly reference: string;
  readonly approvedAt: string;
}

/** Section 9.1(7): bound to the tested hash and the installed deployment record. */
export interface ReleaseAcceptance {
  readonly acceptedBy: string;
  readonly acceptedAt: string;
  readonly deploymentUid: string;
  readonly evidence: string;
}

/** What a release does to the schema, as its build manifest stated it. **Owner decision, 2026-10-19.** */
export interface ReleaseMigrations {
  readonly count: number;
  readonly checksumsSha256: string;
}

export interface PublishedRelease {
  readonly tag: string;
  readonly commit: string;
  readonly artifactSha256: string;
  readonly publishedAt: string;
  /** Required. An entry without one is not offered, so one is always present on a candidate. */
  readonly review: ReleaseReview;
  /** `null` until the artifact has been tested and accepted somewhere. */
  readonly acceptance: ReleaseAcceptance | null;
  /** `null` for an entry published before the migration identity was carried. */
  readonly migrations: ReleaseMigrations | null;
}

export interface ReleaseAvailability {
  readonly installed: { readonly tag: string | null; readonly commit: string | null };
  readonly available: PublishedRelease | null;
  readonly reason:
    | 'up-to-date'
    | 'no-registry-declared'
    | 'registry-unreadable'
    | 'registry-malformed'
    | 'nothing-published'
    | 'installed-unknown'
    | null;
}export interface PlatformHealthSummary {
  readonly sinceUtc: string;
  /**
   * `null` — NOT `0` — when `auditFacility` is `UNREADABLE`.
   *
   * The distinction is the whole point: a zero means "no events in the window", and this screen's
   * backing store is the audit trail itself, so a zero shown when the trail could not be read would
   * be a healthy state about a facility that just failed.
   */
  readonly totalEvents: number | null;
  readonly denials: readonly DenialSummary[];
  readonly totalDenials: number | null;
  readonly jobRuns: readonly JobRunSummary[];
  /** Whether the audit store — this screen's own backing facility — answered. */
  readonly auditFacility: 'READABLE' | 'UNREADABLE';
  /** Most recent LAST. Does NOT come from the audit store, so it survives that store failing. */
  readonly incidents: readonly IncidentSummary[];
  readonly incidentsObserved: number;
  readonly incidentsDropped: number;
  /** Always true in this build: the register is per-process and lost on restart. Shown, not hidden. */
  readonly incidentsProcessLocal: boolean;
  readonly unmeasured: readonly string[];
  /**
   * WHICH MAIL SINK THE SERVER COMPOSED, as its own census names it. **X8 item 3.**
   *
   * Required on the wire, and the server reads it back from the composition census rather than
   * declaring it beside the selection — one of the two arms turns a directory into an unaudited
   * credential store, so the operator must be able to see which one this estate runs.
   *
   * RENDERED SINCE X7, as the `נתיב הדואר היוצא` row on `PlatformHealthScreen.tsx`. X8 declared the
   * field with no row because no Hebrew had been issued and inventing one was forbidden; X7 supplied
   * the label, a value per census state and the unrecognised-name arm this `string` type makes
   * unavoidable. Two facts about that row belong with the field rather than only with the screen:
   * NEITHER census value may read as "mail works" — one arm delivers nothing and the other writes a
   * live credential to a plaintext file — and the row renders in BOTH `auditFacility` arms, because
   * this is a fact about the process and not a read of `audit.*`.
   */
  readonly mailChannel: string;
  /**
   * WHICH PAYMENT PROVIDER THIS ESTATE COMPOSED. **Owner section 8, 2026-11-01.**
   *
   * `none` is an estate that can check no payment method, so self-registration cannot complete on
   * it; `sandbox` is a Staging estate that completes a journey without asking any provider and
   * records that it did; a real provider name is a real terminal. The platform console used to
   * state one of those three in a literal and was therefore wrong on the other two.
   */
  readonly paymentProvider: string;
}

/**
 * The platform's commercial defaults. 08 D4, migration 0016.
 *
 * There is no optional shape here and no nullable field: the read either answers with a policy the
 * server could parse, or it refuses. A `trialDefaultDays: number | null` would be this screen
 * inventing the fallback the service refuses to have.
 */
export interface CommercialPolicySummary {
  readonly trialDefaultDays: number;
  readonly updatedAtUtc: string;
  readonly reason: string;
}

// ── Audit (04 §8.2) ───────────────────────────────────────────────────────────────────────────────

export const AUDIT_OUTCOMES = ['allow', 'deny'] as const;
export type AuditOutcome = (typeof AUDIT_OUTCOMES)[number];

/**
 * THE MIRROR WAS WRONG IN BOTH DIRECTIONS, AND THE `מי` COLUMN WENT BLANK FOR IT. **X10.**
 *
 * This list used to read `user · service · job · anonymous · platform_support`, which is not what
 * anything produces and not what the database permits:
 *
 *   · `CK_audit_actor_kind` (`db/migrations/0004__audit.up.sql:72-73`) admits SEVEN values, and
 *     `AUDIT_ACTOR_KINDS` in `packages/contracts/src/enums.ts` declares those seven verbatim.
 *   · `platform_support` IS NOT ONE OF THEM. It is a PLATFORM ROLE code
 *     (`packages/contracts/src/capabilities.ts`, seeded by `0005`) that was borrowed into an actor
 *     vocabulary — two namespaces, one spelling. No producer writes it and the CHECK would refuse it.
 *   · `platform`, `connector` and `system` were MISSING, and `platform` is the one a tenant most
 *     needs: `domain-audit.ts:103` files a PLATFORM-session act under it, which inside a tenant's
 *     bound context is exactly a support crossing into that office's data. `auditActorKindHe` was an
 *     undefaulted record lookup, so that row's `מי` cell rendered `undefined` — nothing — on the
 *     one screen whose purpose is to show the crossing.
 *
 * Held to `enums.ts` member-for-member by `contract.mirror.test.ts`, and to the migration's CHECK
 * constraint read off disk, so a third spelling of this vocabulary cannot appear.
 */
export const AUDIT_ACTOR_KINDS = [
  'user',
  'platform',
  'service',
  'job',
  'connector',
  'system',
  'anonymous',
] as const;
export type AuditActorKind = (typeof AUDIT_ACTOR_KINDS)[number];

/**
 * EVERY `action` CODE THE OFFICE'S TRAIL CAN SHOW — mirrored from
 * `packages/contracts/src/vocabulary.ts`, where the reason for each exclusion is recorded.
 *
 * Fifteen, enumerated from the PRODUCERS and not from the 21-site class-B inventory: that list
 * counts `#recordAudit` call sites, so it omits the gate's `authz.allowed` / `authz.denied`, which
 * carry the tenant (`packages/authz/src/gate.ts:261`), are the majority of the rows an office sees,
 * and are the ONLY rows that can populate the deny-reason column at all.
 *
 * `auditActionHe` (`./auditActionText`) holds one Hebrew name per member, and the mirror test fails
 * if a member has none.
 */
export const TENANT_VISIBLE_AUDIT_ACTIONS = [
  'authz.allowed',
  'authz.denied',
  'company_access.granted',
  'company_access.revoked',
  'tenant.mfa_policy.set',
  'companies.company.updated',
  'tenant.role.created',
  'tenant.user.roles.assigned',
  'companies.company.created',
  'tenant.user.disabled',
  'security.support_grant.consented',
  'security.support_grant.revoked_by_tenant',
  'auth.session.signed_out',
  'auth.invitation.redeemed',
  'data.cross_tenant.refused',
  // The two recording acts of section 7 (2026-10-28). Mirrored, like every member above them.
  'tenant.invitation.role_intent',
  'tenant.invitation.access_intent',
  // And the refusal of one at redemption (2026-10-28), when the person who chose no longer holds
  // the authority to grant it. Mirrored, like every member above them.
  'tenant.invitation.intent_refused',
] as const;
export type TenantVisibleAuditAction = (typeof TENANT_VISIBLE_AUDIT_ACTIONS)[number];

export interface AuditEventSummary {
  readonly eventUid: string;
  readonly occurredAtUtc: string;
  readonly actorKind: AuditActorKind;
  readonly actorDisplayName: string | null;
  /**
   * THE STABLE CODE, NOT HEBREW. **X10 — the ruled transport, and the reversal of an earlier
   * recommendation.**
   *
   * This field used to be `actionHe: string`, a rendered Hebrew sentence that the server NEVER SENT:
   * `actionHe` existed only in `fixtures.ts`, so in a fixtures-off build the `מה` column — the one
   * saying WHAT HAPPENED — was blank on every row while the table around it rendered fully. The type
   * said `string` and the runtime value was `undefined`: nothing faulted, nothing warned.
   *
   * The code crosses the wire and the Hebrew is rendered HERE, at the edge, by `auditActionHe`
   * (`./auditActionText`) — beside `denialText.ts`, exactly as `denyReason` already is one column
   * over. A server-side Hebrew module would have put this screen's words in two layers, which is the
   * drift this wave adopted its head-carrying guardrail against.
   */
  readonly action: string;
  readonly outcome: AuditOutcome;
  readonly denyReason: DenyReason | null;
  /** Non-null ⇒ the action happened under a platform support grant, and that is never hidden. */
  readonly supportGrantUid: string | null;
}

// ══════════════════════════════════════════════════════════════════════════════════════════════════
// SOURCE CONNECTIONS — the vocabulary of `packages/sources`
//
//   STALENESS_STATES / PARTIAL_REASONS / EMPTY_REASONS / CANONICAL_ORIGINS
//                                        ←  packages/sources/src/canonical.ts
//   CONNECTOR_STATUSES                   ←  packages/sources/src/binding.ts
//   SOURCE_FAILURE_KINDS                 ←  packages/sources/src/adapter.ts (`SourceFailure`)
//
// Nothing here is invented. 07 §6.2 already separates the two watermarks the product must never
// confuse — `dataAsOfUtc`, the latest business date present in the data, and `fetchedAtUtc`, when we
// last successfully talked to the source — because a successful pull that returns nothing advances
// the second and MUST NOT advance the first. That is the mechanism that let a canonical freeze stay
// invisible for a month, and it is why the freshness line on the screen quotes the DATA watermark.
// ══════════════════════════════════════════════════════════════════════════════════════════════════

export const STALENESS_STATES = ['FRESH', 'LAGGING', 'STALE', 'UNKNOWN'] as const;
export type StalenessState = (typeof STALENESS_STATES)[number];

export const PARTIAL_REASONS = [
  'unbalanced',
  'rows_rejected_at_boundary',
  'page_limit_reached',
  'value_absent_in_set',
] as const;
export type PartialReason = (typeof PARTIAL_REASONS)[number];

export const EMPTY_REASONS = [
  'no_data_for_period',
  'capability_not_supplied',
  'source_returned_nothing',
] as const;
export type EmptyReason = (typeof EMPTY_REASONS)[number];

export type CompletenessSummary =
  | { readonly kind: 'COMPLETE' }
  | { readonly kind: 'PARTIAL'; readonly reason: PartialReason }
  | { readonly kind: 'EMPTY'; readonly reason: EmptyReason };

/**
 * `live_source` vs `development_fixture` — the one branch a screen is invited to make, and the
 * specific defence against a demo surface being mistaken for a proven integration.
 */
export const CANONICAL_ORIGINS = ['live_source', 'development_fixture'] as const;
export type CanonicalOrigin = (typeof CANONICAL_ORIGINS)[number];

export const CONNECTOR_STATUSES = [
  'PROVISIONING',
  'ACTIVE',
  'SUSPENDED',
  'FAILED',
  'REVOKED',
] as const;
export type ConnectorStatus = (typeof CONNECTOR_STATUSES)[number];

export const SOURCE_FAILURE_KINDS = [
  'NOT_CONFIGURED',
  'NOT_PROVEN',
  'UNREACHABLE',
  'NOT_AUTHENTICATED',
  'NOT_AUTHORIZED_AT_SOURCE',
  'CAPABILITY_NOT_SUPPLIED',
  'EXTERNAL_COMPANY_UNKNOWN',
  'REJECTED_AT_BOUNDARY',
  'ADAPTER_FAULT',
] as const;
export type SourceFailureKind = (typeof SOURCE_FAILURE_KINDS)[number];

/**
 * The three states the connections list shows, and the first one is the one that matters.
 *
 * `NOT_CONFIGURED` and `ERROR` are DIFFERENT ANSWERS to different questions — "this was never set
 * up" against "this was set up and it is broken" — and 07 N-5 is why they may never collapse: a
 * missing `data_source_type` silently became a working default on 5 of 313 companies, and those
 * five behaved as though they were configured. One amber "problem" chip is that defect in a nicer
 * font.
 *
 * The state is STATED BY THE SERVER, alongside the richer `failureKind` it derived it from. This
 * app renders it. It does not recompute it from the other fields.
 */
export const SOURCE_CONNECTION_STATES = ['NOT_CONFIGURED', 'OK', 'ERROR'] as const;
export type SourceConnectionState = (typeof SOURCE_CONNECTION_STATES)[number];

/**
 * 07 §6.2 F-1..F-5. `null` is the honest answer for a connection that has never observed anything;
 * it is never rendered as a zero, an epoch date, or "now".
 */
export interface FreshnessSummary {
  readonly state: StalenessState;
  /** The latest BUSINESS date present in the data. This is the one the user is shown. */
  readonly dataAsOfUtc: string | null;
  /** When we last successfully talked to the source. Never the headline: a zero-row pull moves it. */
  readonly fetchedAtUtc: string | null;
  /** From the schedule + SLA. `null` ⇒ no basis for a verdict, which is `UNKNOWN` and not `FRESH`. */
  readonly expectedByUtc: string | null;
}

export interface SourceConnectionSummary {
  readonly connectionUid: string;
  /** What the office calls this connection. Never a host, never a connection string (07 §2.3). */
  readonly displayNameHe: string;
  readonly state: SourceConnectionState;
  readonly connectorStatus: ConnectorStatus;
  readonly origin: CanonicalOrigin;
  /** Present on `ERROR` and on `NOT_CONFIGURED`; `null` on `OK`. */
  readonly failureKind: SourceFailureKind | null;
  /**
   * EVERY absent configuration field, not the first — an operator fixing one at a time is an
   * operator who runs the probe five times (07 N-5).
   */
  readonly missingConfiguration: readonly string[];
  /** `null` ⇒ no watermark exists yet, which a never-configured connection must say in words. */
  readonly freshness: FreshnessSummary | null;
  readonly completeness: CompletenessSummary;
  /** How many companies in this office are bound to this connection, as the server counted them. */
  readonly boundCompanyCount: number;
}

// ══════════════════════════════════════════════════════════════════════════════════════════════════
// SECURITY / MFA — `packages/authn`
// ══════════════════════════════════════════════════════════════════════════════════════════════════

/** `packages/authn/src/totp.ts` — RFC 6238 parameters, restated so a screen can label its fields. */
export const TOTP_DIGITS = 6;
export const TOTP_TIME_STEP_SECONDS = 30;
export const RECOVERY_CODE_COUNT = 10;

export const TENANT_MFA_POLICIES = ['optional', 'required_for_admins', 'required_for_all'] as const;
export type TenantMfaPolicy = (typeof TENANT_MFA_POLICIES)[number];

/**
 * What the enrolment screen is handed.
 *
 * Note what is NOT here: no password, ever (04 §2.2 — "no path may ever produce, transmit or
 * display a password"), and no QR image this app draws for itself. The provisioning URI is built by
 * the server from an issuer that is CONFIGURATION; a product name written into this tree would be
 * baked into every enrolled device, not merely into the source.
 */
export interface MfaEnrolmentOffer {
  /** Base32, shown in groups so it can be typed by hand when no camera is available. */
  readonly secretBase32: string;
  /** The account label the authenticator will show. Supplied by the server. */
  readonly accountLabel: string;
  /** Rendered by the server; absent until it is. The screen says so rather than drawing a fake one. */
  readonly qrImageDataUri: string | null;
  /** Shown ONCE. `packages/authn/src/recovery-codes.ts` stores only their hashes. */
  readonly recoveryCodes: readonly string[];
  readonly policy: TenantMfaPolicy;
  /** `product_floor` for a platform account — no tenant policy can lower it. */
  readonly requiredBy: 'product_floor' | 'tenant_policy' | 'not_required';
}

/** The three answers `verifyTotp` can give for a rejected code, plus acceptance. */
export const MFA_CHALLENGE_OUTCOMES = ['accepted', 'malformed', 'incorrect', 'replayed'] as const;
export type MfaChallengeOutcome = (typeof MFA_CHALLENGE_OUTCOMES)[number];

// ══════════════════════════════════════════════════════════════════════════════════════════════════
// PRODUCT STATES THE SERVER ALREADY KNOWS
//
// ── WHY THIS SECTION EXISTS ───────────────────────────────────────────────────────────────────────
//
// Owner direction, verbatim: *"Do not collapse materially different states into generic 403/409/500
// when the server already knows the actionable reason."*
//
// Every type below is a state the server COMPUTED and put on the wire, and each one is a state that
// a generic error would have destroyed:
//
//   · a capacity refusal carries which capacity, the count, the limit and where to go — 08 §7;
//   · a role-assignment refusal carries `excess`, the capabilities that could not be granted;
//   · an invitation answers 202 with `delivery` when the mail port suppressed or failed the message,
//     because *"a caller who is told 200 when nobody receives anything has no way to tell that from a
//     spam folder"*;
//   · MFA enrolment answers 403 with `need`, which is disclosable — the caller is already
//     authenticated, and naming the challenge is the difference between a product and a locked door;
//   · a lifecycle refusal carries the CURRENT status and the permitted moves FROM WHERE THE OBJECT
//     ACTUALLY IS, so a screen offers the three buttons that work instead of printing "refused";
//   · a source connection separates NOT CONFIGURED from ERROR, which 07 N-5 measured the cost of.
//
// ── AND WHAT THEY ARE NOT ─────────────────────────────────────────────────────────────────────────
//
// None of these is a `DenyBody`. The gate had already ALLOWED the request when they were produced —
// the caller holds the capability and is inside their tenant. They describe why the WRITE could not
// happen, which is a different question from who may ask, and they travel as conflicts (or as a 202)
// for exactly that reason. Nothing on this side evaluates one.
//
// Every reason string is mirrored from the file that produces it, and each mapper module's test reads
// that file off disk — the same discipline as `contract.mirror.test.ts`, applied per state.
// ══════════════════════════════════════════════════════════════════════════════════════════════════

// ── Subscription creation (packages/db/src/provisioning-store.ts) ────────────────────────────────

/**
 * WHY A SUBSCRIPTION WAS NOT OPENED, and the three answers are three different next actions.
 *
 * All three used to arrive as `409 subscription_exists`, including the one that is true on a fresh
 * estate: **no plan exists at all.** The Owner's own direction, quoted at the head of this section,
 * is the rule that was broken — the server already knew the reason and reported a different one.
 *
 *   `catalogue_empty`      no ACTIVE plan exists in the catalogue. A configuration state, not a
 *                          conflict about this customer.
 *   `plan_unknown`         the code that was sent is not an active plan.
 *   `subscription_exists`  this tenant already has a live subscription.
 *
 * Mirrored from `packages/db/src/provisioning-store.ts`'s `SubscriptionRefusal`, and
 * `contract.mirror.test.ts` reads that file off disk so the two cannot drift.
 */
export const SUBSCRIPTION_REFUSALS = ['plan_unknown', 'catalogue_empty', 'subscription_exists'] as const;
export type SubscriptionRefusalReason = (typeof SUBSCRIPTION_REFUSALS)[number];

export function isSubscriptionRefusal(value: unknown): value is SubscriptionRefusalReason {
  return typeof value === 'string' && (SUBSCRIPTION_REFUSALS as readonly string[]).includes(value);
}

// ── Mail delivery (packages/mail/src/port.ts) ─────────────────────────────────────────────────────

/**
 * `sent · suppressed · failed`, and they are three outcomes rather than a boolean on purpose:
 * `port.test.ts` exists to keep them distinguishable. `suppressed` is the development sink and any
 * deliberate hold; `failed` is a provider that refused. The office needs different words for each.
 */
export const MAIL_OUTCOMES = ['sent', 'suppressed', 'failed'] as const;
export type MailOutcome = (typeof MAIL_OUTCOMES)[number];

/**
 * What the 202 body's `delivery` field carries. The server builds it as `` `mail_${outcome}` ``, so
 * the only values that can ever arrive are the two non-accepted outcomes.
 */
export const MAIL_DELIVERY_REASONS = ['mail_suppressed', 'mail_failed'] as const;
export type MailDeliveryReason = (typeof MAIL_DELIVERY_REASONS)[number];

/**
 * An invitation that was WRITTEN and whose message did not go out.
 *
 * The invitation exists — `invitationUid` is real, the token was minted, the row is in the database.
 * What did not happen is the delivery, and 202 is the honest status for exactly that: accepted, not
 * completed. `null` delivery is the 200 path, where the message was accepted by the provider.
 */
export interface InvitationResult {
  readonly invitationUid: string;
  /** `null` ⇒ accepted for delivery. Otherwise the office must be told, and told which. */
  readonly delivery: MailDeliveryReason | null;
  /** When the invitation stops resolving. A resend mints a NEW token and this moves. */
  readonly expiresAtUtc: string | null;
}

// ── Step-up (apps/api/src/services/authentication.ts) ─────────────────────────────────────────────

/**
 * The three challenges a step-up can require. Disclosable by design: the caller is already
 * authenticated, so naming WHICH one is safe and is the whole of the difference between an
 * actionable screen and a 403.
 *
 *   REAUTH    the session is too old for this action — sign in again
 *   MFA       a second factor is enrolled and has not been presented in this session
 *   ENROLMENT no second factor exists yet, and this action requires one
 */
export const STEP_UP_NEEDS = ['REAUTH', 'MFA', 'ENROLMENT'] as const;
export type StepUpNeed = (typeof STEP_UP_NEEDS)[number];

// ── Lifecycle refusals ────────────────────────────────────────────────────────────────────────────

/**
 * A tenant lifecycle action that could not be applied — `refuse()` in `routes/commercial.ts`.
 *
 * `currentStatus` and `permittedFrom` are the actionable half. "Refused" tells an operator nothing;
 * "this customer is already suspended" tells them the job is done, and "suspension is permitted from
 * active or trial" tells them what would have to change first.
 */
export interface TenantLifecycleRefusal {
  readonly reason: string;
  /** Where the tenant actually is. Absent when the refusal is not about a status at all. */
  readonly currentStatus: TenantStatus | null;
  /** The statuses the attempted move is permitted FROM. Empty when the refusal is not a transition. */
  readonly permittedFrom: readonly TenantStatus[];
}

/**
 * A company lifecycle transition that could not be applied — `companies.company.lifecycle`.
 *
 * `allowedTransitions` is *"the actionable half: a screen can offer the three buttons that work
 * instead of printing refused"*, in the route's own words. It is the list FROM WHERE THE COMPANY
 * ACTUALLY IS, which is why it can differ from what the screen offered a moment ago.
 */
export interface CompanyTransitionRefusal {
  readonly reason: string;
  readonly from: CompanyLifecycleStatus | null;
  readonly allowedTransitions: readonly CompanyLifecycleStatus[];
}

// ── Sessions (packages/authn/src/session-secret.ts) ───────────────────────────────────────────────

export const SESSION_REVOCATION_REASONS = [
  'user_signed_out',
  'password_changed',
  'mfa_changed',
  'recovery_code_used',
  'membership_disabled',
  'administrator_revoked',
  /**
   * The TENANT was terminated, which is a different fact from a person acting on one account —
   * `administrator_revoked` names an actor and a scope that would both be wrong for an offboarding
   * cascade. `packages/connector` already had `tenant_closed`, and the two halves of one event should
   * not be describable in only one of them.
   */
  'tenant_terminated',
  'suspected_compromise',
] as const;
export type SessionRevocationReason = (typeof SESSION_REVOCATION_REASONS)[number];

/**
 * The three an ADMINISTRATOR may assert — `ADMIN_ASSERTABLE_REVOCATION_REASONS`, which the server
 * DERIVES (everything not automatic, minus `user_signed_out`) rather than hand-listing.
 *
 * The rest are facts the system observes. An administrator who could assert `password_changed` would
 * be writing a password change that never happened into the audit trail; one who could assert
 * `user_signed_out` would be attributing an eviction to the person it was performed on. The service
 * refuses anything outside this set and returns the set itself as the `excess`.
 */
export const ADMIN_ASSERTABLE_REVOCATION_REASONS: readonly SessionRevocationReason[] = [
  'administrator_revoked',
  'tenant_terminated',
  'suspected_compromise',
];

/**
 * One row of "where this account is signed in".
 *
 * There is no IP address and no user-agent string here, and their absence is the design: this list is
 * read by an administrator ABOUT A COLLEAGUE (`tenant.user.session.read`, audited ALWAYS because
 * reading it is surveillance of a person), and the question it has to answer is "how many, since
 * when, and can I evict this one" — not "where does this person work from".
 */
export interface UserSessionSummary {
  readonly sessionUid: string;
  readonly startedAtUtc: string;
  readonly lastSeenAtUtc: string | null;
  readonly expiresAtUtc: string | null;
  /** `true` for the session making the request. Only ever set on the caller's OWN list. */
  readonly current: boolean;
  /** Non-null ⇒ already revoked, and the row stays visible with the reason (02 §8: no delete). */
  readonly revokedReason: SessionRevocationReason | null;
}

// ── Connector credentials (packages/connector/src/credential.ts) ──────────────────────────────────

export const CREDENTIAL_REVOCATION_REASONS = [
  'suspected_compromise',
  'operator_revoked',
  'connector_decommissioned',
  'tenant_closed',
  'enrolment_disputed',
  'rotation_forced',
] as const;
export type CredentialRevocationReason = (typeof CREDENTIAL_REVOCATION_REASONS)[number];

/**
 * The two that mean PAST traffic is in question, not merely that future traffic stops.
 * A revoked credential says different things to an office depending on which side of this line it is
 * on, and one sentence for both would be the collapse this whole section exists against.
 */
export const RETROSPECTIVE_REVOCATION_REASONS: readonly CredentialRevocationReason[] = [
  'suspected_compromise',
  'enrolment_disputed',
];

// ── Connector health (packages/connector/src/health.ts) ───────────────────────────────────────────

/**
 * CONTACT health, and `NEVER_CONNECTED` is not a kind of `DISCONNECTED`.
 *
 * The first is an installation problem on the customer's machine; the second worked yesterday and is
 * an operations problem. Same colour on a dashboard, different person, different hour — and roughly
 * 24,000 items queued with nothing ever ingested is what one chip for both was worth.
 */
export const CONNECTOR_HEALTH_STATES = [
  'NEVER_CONNECTED',
  'UNMEASURABLE',
  'CONNECTED',
  'DEGRADED',
  'DISCONNECTED',
] as const;
export type ConnectorHealthState = (typeof CONNECTOR_HEALTH_STATES)[number];

export const DEGRADATION_CODES = [
  'CREDENTIAL_ROTATION_DUE',
  'PROTOCOL_VERSION_SUNSETTING',
  'CLOCK_SKEW_NEAR_WINDOW',
  'SOURCE_UNREACHABLE',
  'SOURCE_REFUSING_REQUESTS',
  'BINDINGS_UNMAPPED',
] as const;
export type DegradationCode = (typeof DEGRADATION_CODES)[number];

/**
 * `contactIsNotDataFreshness` is always `true` and is carried so a consumer cannot claim it was not
 * told. A green connector above a stale trial balance is the June 2026 freeze exactly, and this
 * payload therefore has no data watermark on it at all.
 */
export interface ConnectorHealthSummary {
  readonly connectionUid: string;
  readonly state: ConnectorHealthState;
  readonly lastSeenAtUtc: string | null;
  readonly degradations: readonly DegradationCode[];
  readonly contactIsNotDataFreshness: true;
}

// ── Source administration refusals (apps/api/src/services/source-admin.ts) ────────────────────────

/**
 * A configuration that was refused. `missingConfiguration` names EVERY absent field and never the
 * first — 07 N-5: an operator fixing one at a time is an operator who runs the probe five times.
 */
export interface SourceConfigurationRefusal {
  readonly reason: string;
  readonly missingConfiguration: readonly string[];
  /** The service's own sentence. Rendered only when this build has no Hebrew for `reason`. */
  readonly remedy: string | null;
}

/**
 * The result of a connectivity check. 200 EVEN WHEN THE SOURCE FAILED: a probe that RAN produced an
 * answer, and turning "the customer's system is off" into a 5xx makes it indistinguishable from "our
 * product is broken". `diagnostic` is deliberately absent from this mirror — it is for an operator
 * and for the audit trail, no caller may parse one, and the screen renders the KIND.
 */
export type ProbeOutcome =
  | { readonly ok: true; readonly checkedAtUtc: string | null }
  | { readonly ok: false; readonly failureKind: SourceFailureKind };

/**
 * How the date arrives from the source, and how strong an identifier assertion is.
 *
 * Both are the SERVER'S closed lists, mirrored because a mapping form has to offer them. There is no
 * "guess" member in either: 07 §2.3 makes the date format part of the connection's configuration
 * rather than something an importer sniffs per file, and `RESOLVABLE_CONFIDENCES` on the server side
 * is what decides which assertions may be acted on — never a screen.
 */
export const SOURCE_DATE_FORMATS = ['ISO_8601', 'DAY_FIRST_SLASH', 'COMPACT_YYYYMMDD'] as const;
export type SourceDateFormat = (typeof SOURCE_DATE_FORMATS)[number];

export const IDENTIFIER_CONFIDENCES = [
  'authoritative',
  'human_confirmed',
  'derived_verified',
  'legacy_unverified',
  'proposed',
  'disputed',
] as const;
export type IdentifierConfidence = (typeof IDENTIFIER_CONFIDENCES)[number];

/** One company as the SOURCE names it. Never normalised — 05 R-5: `a049` and `a49` are two clients. */
export interface ExternalCompanySummary {
  /** Opaque text in the source's namespace. Shown LTR, never trimmed, never case-folded. */
  readonly externalCompanyId: string;
  readonly displayName: string;
  /** The internal company it is bound to, or `null` — which is the onboarding queue, as a state. */
  readonly boundCompanyUid: string | null;
}

/**
 * Why a binding could not be made or resolved.
 *
 * `not_found` deliberately covers an unknown connector AND a cross-tenant one together (S-12): a
 * caller able to tell them apart can enumerate another office's connectors one probe at a time. The
 * collapse is the SERVER'S, made once in `binding.ts`, and this side renders whichever answer arrived.
 */
export const BINDING_REFUSALS = ['not_found', 'unmapped', 'ambiguous', 'unavailable'] as const;
export type BindingRefusalReason = (typeof BINDING_REFUSALS)[number];

export interface BindingRefusal {
  readonly reason: BindingRefusalReason;
  /**
   * 0002 rule 3: *a conflict that cannot say what would settle it is a conflict nobody can close.*
   * `null` only where the refusal is not a conflict between candidates.
   */
  readonly resolvingFactRequired: string | null;
  /** How many internal companies claim this external one. Never WHICH — those are internal ids. */
  readonly candidateCount: number;
}

// ── Support grants (04 §13.1.5 / §6.4) ────────────────────────────────────────────────────────────

export const SUPPORT_GRANT_MODES = ['READ_ONLY', 'READ_WRITE'] as const;
export type SupportGrantMode = (typeof SUPPORT_GRANT_MODES)[number];

/**
 * What still blocks a grant from being usable. A grant is created UNAPPROVED and, where the customer
 * requires it, WITHOUT CONSENT — so the ordinary state of a fresh grant is "blocked by two things",
 * and a screen that showed only "pending" would hide which of them the operator can act on.
 */
export const SUPPORT_GRANT_BLOCKERS = [
  'awaiting_approval',
  'awaiting_consent',
  'expired',
  'revoked',
] as const;
export type SupportGrantBlocker = (typeof SUPPORT_GRANT_BLOCKERS)[number];

export interface SupportGrantSummary {
  readonly grantUid: string;
  /** The customer the grant reaches into. Named, always — a grant with no named tenant is a key. */
  readonly tenantUid: string;
  readonly tenantLegalName: string;
  /**
   * WHO ASKED, AS A LABEL.
   *
   * NULLABLE, and it was declared non-null while nothing produced it at all. `core.app_user.
   * display_name` is `NVARCHAR(128) NOT NULL`, but the producer reaches it through a LEFT JOIN
   * (`support-access-store.ts`, `listGrants`), so a grant whose requester row is no longer
   * joinable answers `null` - and such a grant must still appear in the crossing list rather than
   * vanish from it. Same reason, same answer as `requestedByUserUid` below.
   */
  readonly requestedByDisplayName: string | null;
  /**
   * WHO ASKED, AS AN IDENTITY. **X6 (R-3), UX B4.**
   *
   * `requestedByDisplayName` is a LABEL. Two platform operators may share a display name, a display
   * name may be edited, and comparing one to the signed-in user's is not an identity check — so it
   * cannot decide whether a row is the viewer's own request. This can.
   *
   * It is used for exactly one thing: suppressing the approve control on the viewer's OWN rows, so
   * that self-approval is visibly impossible rather than only refused on submit. That suppression is
   * an AFFORDANCE. The control is the store's `requested_by_user_id != @approver` predicate
   * (`packages/db/src/support-access-store.ts:340`) plus `CK_support_grant_dual_control`, and it
   * holds for a caller holding every capability with no screen in the process at all.
   *
   * NULLABLE because the producer's join is a LEFT JOIN (`support-access-store.ts:172`): a grant
   * whose requester row is gone still has to appear in the crossing list. `null` is never treated as
   * a match — an unknown requester is not "me".
   */
  readonly requestedByUserUid: string | null;
  readonly reason: string;
  readonly mode: SupportGrantMode;
  readonly startsAtUtc: string;
  readonly expiresAtUtc: string;
  /** Empty ⇒ the grant is live. Anything in it means the gate is still refusing. */
  readonly blockedBy: readonly SupportGrantBlocker[];
  /**
   * OA-0002: a falsifiable assertion attributable to a named requester, never a defaulted note.
   * Shown because a related-party support access is the one an auditor will ask about first.
   */
  readonly isRelatedParty: boolean;
}

/**
 * ONE SUPPORT GRANT, AS THE OFFICE SEES IT. **CM-012.**
 *
 * The tenant-side counterpart of `SupportGrantSummary` above, and the differences between the two
 * are the whole point. This one names no tenant — there is only one it could be, and a field for it
 * would be a field somebody could put another value in. It carries no requester and no approval
 * trail: who at GL-POINT asked for the grant and which second principal approved it is OUR internal
 * governance, not a fact about the customer.
 *
 * What it does carry is everything the office needs to act: why, for how long, whether its own
 * consent is outstanding, and whether it has already revoked.
 */
export interface TenantSupportGrantView {
  readonly grantUid: string;
  readonly reason: string;
  readonly ticketRef: string | null;
  readonly mode: SupportGrantMode;
  /** True ⇒ the grant is dead until this office consents, whatever GL-POINT has approved. */
  readonly requiresConsent: boolean;
  readonly consentGivenAtUtc: string | null;
  readonly startsAtUtc: string;
  readonly expiresAtUtc: string;
  readonly revokedAtUtc: string | null;
}

// ══════════════════════════════════════════════════════════════════════════════════════════════════
// OFFBOARDING — what happens when a customer leaves.
//
//   OFFBOARDING_STAGES / Reversal / OffboardingStage / RevocationTally / PurgeEligibility
//   RetentionPolicySummary / ExportDisposition   ←  apps/api/src/services/tenant-lifecycle.ts
//   RESIDENCY_CATEGORIES                         ←  packages/sources/src/residency.ts
//
// ── WHY THIS MIRROR IS A UNION AND NOT A FLAG ─────────────────────────────────────────────────────
//
// The service is emphatic, and the shape is the argument: *"A boolean cannot carry the one thing a
// caller must know before acting: what UNDOING this costs."* A screen that reduced `Reversal` to
// `canUndo: boolean` would answer `false` for a tenant whose reversibility is simply NOT KNOWABLE —
// there is no readable retention policy — and an operator reading "cannot be undone" about a
// customer terminated in error would go and re-create them as a new tenant, which loses the audit
// thread, the company identity graph and the conflict history that reinstatement would have kept.
//
// So the union is mirrored member for member, `ACTIVE` carries NO `reversal` key at all, and
// `offboardingText.ts` gives each member its own Hebrew. `contract.mirror.test.ts` reads the service
// off disk and fails if a member is added, removed or renamed there and not here.
//
// ── AND THE ABSENCE THAT IS THE POINT ─────────────────────────────────────────────────────────────
//
// There is NO `purge` action type here, because there is no purge ROUTE and no capability that could
// authorise one. `PurgeEligibility` is an ANSWER: it says whether the governed deletion workflow may
// begin and, when it may not, exactly what is blocking it. `eligible: true` is not permission to
// delete — it is the removal of the offboarding service's objections.
// ══════════════════════════════════════════════════════════════════════════════════════════════════

export const OFFBOARDING_STAGES = ['ACTIVE', 'SUSPENDED', 'TERMINATED', 'PURGED'] as const;
export type OffboardingStageName = (typeof OFFBOARDING_STAGES)[number];

export const REVERSAL_KINDS = [
  'FREE',
  'GOVERNED_WINDOW',
  'CLOSED',
  'UNDETERMINABLE',
  'IRREVERSIBLE',
] as const;
export type ReversalKind = (typeof REVERSAL_KINDS)[number];

/**
 * WHY reversibility is not assertable. Two different configuration failures, and the screen must
 * name which — "there is no policy" is fixed by writing one, "the policy does not hold together" is
 * fixed by correcting the one that exists.
 */
export const UNDETERMINABLE_REASONS = [
  'retention_policy_absent',
  'retention_policy_invalid',
] as const;
export type UndeterminableReason = (typeof UNDETERMINABLE_REASONS)[number];

/**
 * WHAT UNDOING THIS STAGE COSTS.
 *
 * `restores: 'nothing'` on `GOVERNED_WINDOW` is not a placeholder and the screen must not soften it:
 * reinstatement gives back the RELATIONSHIP and not one unit of access.
 */
export type Reversal =
  | { readonly kind: 'FREE' }
  | { readonly kind: 'GOVERNED_WINDOW'; readonly until: string; readonly restores: 'nothing' }
  | { readonly kind: 'CLOSED'; readonly because: 'reinstatement_window_closed' }
  | { readonly kind: 'UNDETERMINABLE'; readonly because: UndeterminableReason }
  | { readonly kind: 'IRREVERSIBLE' };

/**
 * WHAT BECAME UNREACHABLE — a count per class, never a list.
 *
 * Counts and not identities, for the service's own reason: a list of session uids or credential
 * fingerprints in an immutable audit row is a durable disclosure of exactly the material the session
 * cookie was split to avoid printing.
 */
export interface RevocationTally {
  readonly connectorCredentials: number;
  readonly connectors: number;
  readonly sourceConnections: number;
  readonly invitations: number;
  readonly sessions: number;
}

/**
 * The five classes, IN THE ORDER THE SERVER REVOKES THEM — decreasing autonomy: the unattended
 * machine first, the people last. The screen lists them in this order because that order is the
 * answer to "what happened first", and re-sorting it alphabetically would destroy the one property
 * the list carries beyond its numbers.
 */
export const REVOCATION_CLASSES = [
  'connectorCredentials',
  'connectors',
  'sourceConnections',
  'invitations',
  'sessions',
] as const;
export type RevocationClass = (typeof REVOCATION_CLASSES)[number];

/**
 * The stage, as a value that CANNOT BE READ WITHOUT NARROWING.
 *
 * `ACTIVE` has no `reversal` key, so a screen cannot ask "is this reversible?" of a stage where the
 * question is meaningless and get `false` by accident. `revoked` appears on `TERMINATED` and nowhere
 * else, because only termination makes anything unreachable.
 */
export type OffboardingStage =
  | { readonly stage: 'ACTIVE' }
  | {
      readonly stage: 'SUSPENDED';
      readonly reversal: Extract<Reversal, { kind: 'FREE' }>;
      readonly enteredAt: string;
      readonly reason: string;
    }
  | {
      readonly stage: 'TERMINATED';
      readonly reversal: Reversal;
      readonly enteredAt: string;
      readonly reason: string;
      readonly revoked: RevocationTally;
    }
  | {
      readonly stage: 'PURGED';
      readonly reversal: Extract<Reversal, { kind: 'IRREVERSIBLE' }>;
      readonly enteredAt: string;
      readonly reason: string;
    };

/** Everything the governed deletion workflow is waiting for. Never one blocker only. */
export const PURGE_BLOCKERS = [
  'not_terminated',
  'already_purged',
  'retention_policy_absent',
  'retention_policy_invalid',
  'retention_period_running',
  'export_obligation_outstanding',
] as const;
export type PurgeBlocker = (typeof PURGE_BLOCKERS)[number];

export type PurgeEligibility =
  | {
      readonly eligible: false;
      readonly blockedBy: readonly PurgeBlocker[];
      /** Present when the only thing standing in the way is the clock. */
      readonly notBefore?: string;
    }
  | {
      readonly eligible: true;
      readonly notBefore: string;
      /** `describeResidency`'s sentence. Named in Hebrew by a mapper; never rendered verbatim. */
      readonly residency: string;
      /** What the policy's author stated deletion would mean for this customer's data. */
      readonly deletionImplication: string;
    };

/**
 * Every way a retention policy can fail to hold together. The residency half arrives as
 * `` `residency_${violation}` `` from the sources package, which is an open suffix — so the type is a
 * union with a template member and the mapper answers `null` for one this build has no words for.
 */
export const RETENTION_POLICY_VIOLATIONS = [
  'basis_blank',
  'deletion_eligibility_not_a_day_count',
  'reinstatement_window_not_a_day_count',
  'reinstatement_window_outlives_retention',
] as const;
export type RetentionPolicyViolation =
  | (typeof RETENTION_POLICY_VIOLATIONS)[number]
  | `residency_${string}`;

/** Mirrored so a screen can name the category rather than print the server's boot-report sentence. */
export const RESIDENCY_CATEGORIES = ['LIVE_READ', 'CACHE', 'SNAPSHOT', 'GLPOINT_OWNED'] as const;
export type ResidencyCategory = (typeof RESIDENCY_CATEGORIES)[number];

/**
 * The policy as a screen may see it.
 *
 * NOTICE WHAT IS ABSENT: no company, no user, no amount, no ledger figure. And notice that there is
 * no day count with a default — `deletionEligibleAfterDays` is CONFIGURED, and a `null` policy is
 * rendered as the absence it is rather than as an unconstrained one.
 */
export interface RetentionPolicySummary {
  readonly policyUid: string;
  readonly policyVersion: number;
  /** The governing instrument, in the words of whoever set it. Rendered verbatim; it is their text. */
  readonly basis: string;
  readonly deletionEligibleAfterDays: number;
  readonly reinstatementWindowDays: number;
  readonly residency: string;
  readonly setAt: string;
  /** Every reason the policy does not hold together. Empty ⇒ it does. */
  readonly violations: readonly RetentionPolicyViolation[];
}

/** The read model of `platform.tenant.offboarding.read`. Operational metadata, never business data. */
export interface OffboardingStateView {
  readonly stage: OffboardingStage;
  /** `null` ⇒ no governing policy. Rendered as the absence it is, never as a default. */
  readonly retention: RetentionPolicySummary | null;
  readonly purge: PurgeEligibility;
}

/**
 * WHAT HAPPENS TO THE CUSTOMER'S DATA EXPORT — declared, never defaulted.
 *
 * The route parses this and answers a malformed or absent one as a bad request; it does NOT become
 * `WAIVED`. The customer's right to their data survives termination, and *"a right that disappears
 * when a field is omitted is not a right, it is a note"* — so the form has no pre-selected answer.
 */
export const EXPORT_DISPOSITION_KINDS = ['REQUESTED', 'ALREADY_DELIVERED', 'WAIVED'] as const;
export type ExportDispositionKind = (typeof EXPORT_DISPOSITION_KINDS)[number];

export type ExportDisposition =
  | { readonly kind: 'REQUESTED' }
  | { readonly kind: 'ALREADY_DELIVERED'; readonly exportRequestUid: string }
  | { readonly kind: 'WAIVED'; readonly detail: string };

/**
 * WHAT A REINSTATED TENANT HAS TO DO AGAIN. Never empty on a successful reinstatement.
 *
 * `subscription_state` is on it because reinstatement leaves the subscription expired ON PURPOSE:
 * restoring service is a separate commercial decision, and a reinstatement that silently restored it
 * would be a commercial decision made by an offboarding routine.
 */
export const MUST_REESTABLISH_ITEMS = [
  'connector_enrolment',
  'source_connection_credentials',
  'user_sessions',
  'invitations',
  'subscription_state',
] as const;
export type MustReestablishItem = (typeof MUST_REESTABLISH_ITEMS)[number];

/**
 * AN OFFBOARDING ACTION THAT COULD NOT BE APPLIED — `refuse()` in `routes/tenant-lifecycle.ts`.
 *
 * Every field is the actionable half of one refusal, and `revoked` is the one that matters most:
 * *"a cascade that revoked five classes of access and then failed to record its stage returns a
 * refusal that STILL CARRIES THE TALLY, because an operator told only 'concurrent_modification'
 * would not know that every session in the tenant is gone."* A screen that dropped it would show a
 * generic conflict over a tenant whose connector credentials are already dead.
 */
export interface OffboardingRefusal {
  readonly reason: string;
  /** Where the tenant actually is. `null` when the refusal is not about a stage. */
  readonly currentStage: OffboardingStageName | null;
  /** The stages the tenant may move to FROM WHERE IT ACTUALLY IS. Empty when not a transition. */
  readonly allowedStages: readonly OffboardingStageName[];
  /** Names the service that DOES own the arc that was asked for. A code, never rendered raw. */
  readonly arcOwner: string | null;
  /** Every reason the retention policy did not hold together, not the first one. */
  readonly violations: readonly RetentionPolicyViolation[];
  readonly reinstatableUntil: string | null;
  readonly notBefore: string | null;
  /** The refusal `CommercialService` produced, carried through rather than flattened to "refused". */
  readonly commercialReason: string | null;
  /** NON-NULL ⇒ the cascade ran before the refusal. Access is already gone. */
  readonly revoked: RevocationTally | null;
}

// ── user-preference.ts ────────────────────────────────────────────────────────────

/**
 * THE PREFERENCES THIS PRODUCT KEEPS ON THE ACCOUNT. **§6.9, 2026-10-29.**
 *
 * Field for field `PREFERENCE_KEYS` in `apps/api/src/services/user-preference.ts`, which is the
 * CLOSED vocabulary the write route refuses outside of BY NAME. `contract.mirror.test.ts` reads
 * that file off disk and fails if these two lists ever differ, so the mirror is checked rather
 * than trusted — the same arrangement every other list in this file has.
 *
 * THESE ARE NOT THE BROWSER'S STORAGE KEYS. The device holds a copy of the same two orders under
 * `glpoint.homeOrder.main` / `glpoint.homeOrder.side`, and the pairing is stated once, in
 * `shell/preferences.ts`. Two namespaces because they are two different things — an account's
 * preference, and a copy of it on one machine — and one name for both would hide which of them a
 * defect was in.
 */
export const PREFERENCE_KEYS = [
  'home.order.main',
  'home.order.side',
  'home.hidden',
  /* 2026-11-01 section 7: the star, on the account rather than on one machine. */
  'home.favourites',
  /* 2026-11-01: which exchange rates the currency card carries. *"אני רוצה לבחור."* */
  'home.currencies',
] as const;
export type PreferenceKey = (typeof PREFERENCE_KEYS)[number];

// ── work-items.ts ────────────────────────────────────────────────────────────────────────────────

/**
 * THE TASKS MODULE'S SHAPES. **§6, 2026-10-29.**
 *
 * `WorkItemState` mirrors `ITEM_STATES` in `apps/api/src/services/work-items.ts`, and
 * `contract.mirror.test.ts` reads that file off disk and fails if the two lists ever differ — the
 * same arrangement every other closed list in this file has. A drifted list here would compile,
 * type-check, and be answered 422 by the estate, which is the worst shape of defect because every
 * local check passes.
 */
export const WORK_ITEM_STATES = ['open', 'in_progress', 'done', 'cancelled'] as const;
export type WorkItemState = (typeof WORK_ITEM_STATES)[number];

export const WORK_ITEM_KINDS = ['task', 'notice'] as const;
export type WorkItemKind = (typeof WORK_ITEM_KINDS)[number];

/**
 * One row of the office's list.
 *
 * `mayAct` IS THE SERVER'S ANSWER. Not a capability check the browser repeats: the rule is about
 * the row — assigned to me, or raised by me — and the server is the only place that holds both the
 * row and the caller.
 */
export interface WorkItemSummary {
  readonly uid: string;
  readonly itemKind: WorkItemKind;
  /** `automatic` ⇒ a reminder rule raised it, and no person is attributed. */
  readonly origin: 'manual' | 'automatic';
  readonly title: string;
  readonly body: string | null;
  readonly companyUid: string | null;
  readonly moduleCode: string | null;
  readonly createdByDisplayName: string | null;
  readonly assignedMembershipUid: string | null;
  readonly assignedDisplayName: string | null;
  /** A DAY, as `YYYY-MM-DD`. A filing deadline is a day and a time would invent a cut-off. */
  readonly dueOn: string | null;
  readonly itemState: WorkItemState;
  readonly createdAtUtc: string;
  readonly closedAtUtc: string | null;
  readonly readByMe: boolean;
  readonly mayAct: boolean;
}

export interface WorkItemDetailView extends WorkItemSummary {
  readonly comments: readonly {
    readonly authorDisplayName: string;
    readonly body: string;
    readonly createdAtUtc: string;
  }[];
  /** The handling history. `actorDisplayName` is null when the PRODUCT did it. */
  readonly history: readonly {
    readonly eventKind: string;
    readonly fromState: string | null;
    readonly toState: string | null;
    readonly actorDisplayName: string | null;
    readonly occurredAtUtc: string;
  }[];
}

// ── market.ts ────────────────────────────────────────────────────────────────────

/**
 * THE MARKET READINGS. **§4, 2026-10-29.**
 *
 * Mirrors `MARKET_DOMAINS` and `MarketCardState` in the server's own files;
 * `contract.mirror.test.ts` reads them off disk and fails if the lists drift.
 *
 * `state` IS THE SERVER'S ANSWER AND THE CARD RENDERS IT. The four values keep the distinction the
 * Owner drew: *"כשל טעינה או היעדר חיבור לא יוצגו כאפס"* — `not_configured` is nobody having chosen a
 * publisher, `unavailable` is a configured source with no reading yet, and neither is a zero.
 */
export const MARKET_DOMAIN_CODES = [
  'fx',
  'policy_rate',
  'cpi',
  'fuel',
  'professional_updates',
] as const;
export type MarketDomainCode = (typeof MARKET_DOMAIN_CODES)[number];

/**
 * ── ONE BENEFIT, AS THE COMMERCIAL SCREEN READS IT. **2026-11-01.** ───────────────────────────
 *
 * The browser mirror of the service's `BenefitView`. It carries the TERM — what was agreed — and
 * the three governance facts a person needs in order to act on it, and it carries no list price,
 * no amount owed and no net charge: 08 §8.4 keeps what a customer agreed apart from what they
 * pay, and the gate that authorises this read never saw a price.
 *
 * `discountUid` is the string the second approver types. It is the same one `platform.discount.apply`
 * answers with, which is what makes a request made in one session findable in the next.
 */
export interface BenefitView {
  readonly discountUid: string;
  readonly status: 'active' | 'pending_approval';
  readonly term: {
    readonly discountKind: string;
    readonly discountPct: number | null;
    readonly discountAmount: string | null;
    readonly fixedPriceAmount: string | null;
    readonly currencyCode: string | null;
    readonly scope: string;
    readonly scopeSkuCode: string | null;
    readonly effectiveFrom: string;
    readonly effectiveTo: string | null;
  };
  readonly reasonHe: string;
  readonly isRelatedParty: boolean;
  readonly requestedAtUtc: string | null;
}

export const MARKET_CARD_STATES = ['not_configured', 'fresh', 'stale', 'unavailable'] as const;
export type MarketCardStateCode = (typeof MARKET_CARD_STATES)[number];

export interface MarketCardView {
  readonly domainCode: MarketDomainCode;
  readonly state: MarketCardStateCode;
  /** Who publishes it. `null` when nothing is configured — there is nobody to name. */
  readonly publisherHe: string | null;
  /** DECLARED ON THE SOURCE, because the interest answer states no unit at all. */
  readonly valueUnit: string | null;
  readonly series: readonly {
    readonly seriesCode: string;
    readonly seriesLabelHe: string | null;
    readonly valueDecimal: string | null;
    readonly valueText: string | null;
    readonly changePercent: string | null;
    /** The PUBLISHER's date. The only one a card may print as *"נכון ל…"*. */
    readonly observedAtUtc: string;
  }[];
  readonly lastSuccessAtUtc: string | null;
  /** A FLAG AND NOT A REASON: a transport message can carry an internal host name. */
  readonly lastAttemptFailed: boolean;
}

/* ── 0030/0031 · REPORTING DEADLINES ──────────────────────────────────────────────────────────── */

/**
 * ONE RULE AS THE OFFICE SEES IT.
 *
 * `isOwnRule: false` is a PLATFORM TEMPLATE this office has not made its own. All four templates
 * the product ships are in that state, unverified, with `dueDayOfPeriod === null` — because
 * *"אל תמציא מועדים"*, and a number typed to satisfy a column would be indistinguishable
 * afterwards from a number somebody checked.
 *
 * `verificationState` and `isActive` are two facts and not one: the database refuses an active rule
 * that is not verified, so the pair can only ever be unverified+inactive, verified+inactive or
 * verified+active. A screen showing one boolean would have had to choose which to hide.
 */
export interface ReminderRuleView {
  readonly uid: string;
  readonly ruleCode: string;
  readonly displayNameHe: string;
  readonly moduleCode: string;
  readonly periodKind: string;
  readonly dueDayOfPeriod: number | null;
  /** Whole months after the period closes. 1 = the month after, which is the common shape. */
  readonly dueMonthOffset: number;
  readonly leadDays: number;
  readonly sourceNote: string | null;
  readonly isActive: boolean;
  readonly verificationState: string;
  readonly effectiveFrom: string | null;
  readonly effectiveTo: string | null;
  readonly isOwnRule: boolean;
}

/**
 * ONE RULE, AS IT APPLIES — OR DOES NOT — TO ONE CLIENT.
 *
 * Every rule the office sees appears here, including the ones this client does NOT file: the
 * absence of a setting row is the answer, and a list of only the rows that exist could not show an
 * office what it has not yet decided.
 *
 * The four nullable settings are this client's OVERRIDES. `null` means "the rule's", which is why
 * they are nullable here and not resolved: a screen that showed the resolved value could not tell
 * an office which clients it had actually decided about.
 */
export interface CompanyReminderSettingView {
  readonly ruleUid: string;
  readonly ruleCode: string;
  readonly displayNameHe: string;
  readonly ruleVerificationState: string;
  readonly ruleIsActive: boolean;
  readonly isEnabled: boolean;
  readonly periodKind: string | null;
  readonly dueDayOfPeriod: number | null;
  readonly dueMonthOffset: number | null;
  readonly leadDays: number | null;
  readonly settingsNote: string | null;
  /** Who turned it on is on the row; this is when. Null whenever it is not enabled. */
  readonly enabledAtUtc: string | null;
}

/* ── 0032 · THE PUBLIC DOOR ──────────────────────────────────────────────────────────────────── */

/**
 * ONE PLAN AS A STRANGER IS SHOWN IT. §9.6: *"מחיר, מועד חיוב ראשון ומחזור חיוב בבירור."*
 *
 * Every figure is the SERVER'S. The screen displays them and sends none of them back: a price the
 * browser returned would be a price the browser chose, and the consent record would be worth
 * nothing. `complete` re-resolves the offer server-side and records what it resolved.
 */
/**
 * THE CLEARING CONNECTION AS AN OPERATOR SEES IT. 0041.
 *
 * ── `instruments` IS THE INTERESTING FIELD AND IT IS NOT A TOTAL ───────────────────────────────
 *
 * It is a count PER PROVIDER, and the split is the whole value of it: an estate that ran for a
 * while with the sandbox adapter has offices whose card NOBODY EVER CHECKED, and they are
 * billable and will fail at the first real charge. A single number would hide exactly the ones an
 * operator needs to find before the customer does.
 *
 * ── AND `unresolved` IS SEPARATE FROM `recent` FOR A REASON ───────────────────────────────────
 *
 * A DECLINE IS NOT A FAILURE THAT NEEDS A PERSON: it is a commercial fact, and the dunning policy
 * handles it on the Owner's own cadence. `unresolved` is the other thing — a charge whose outcome
 * at the provider is UNKNOWN, with the idempotency key already spent, which is neither collected
 * nor uncollected until somebody reads it back. There should be none; a handful is an incident.
 */
export interface ClearingOverviewView {
  /** `none` | `sandbox` | `cardcom`. The PORT's name, read back from the composed process. */
  readonly provider: string;
  readonly configured: boolean;
  readonly instruments: readonly { readonly provider: string; readonly held: number }[];
  readonly unresolved: readonly ClearingAttemptView[];
  readonly recent: readonly ClearingAttemptView[];
}

/**
 * ONE CHARGE ATTEMPT ON THE OPERATOR'S SCREEN.
 *
 * `providerTransactionId` IS A STRING. It is `int64` at the provider and in our column, and a
 * JavaScript number loses integers above 2^53 — a transaction id one digit different from the
 * provider's is worse than none, because an operator would reconcile against it and find nothing.
 */
export interface ClearingAttemptView {
  readonly chargeAttemptUid: string;
  /**
   * WHOSE CHARGE IT IS. The Owner, 2026-11-02: *"כמנהל פלטפורמה אני צריך לראות את כל החיובים של
   * כל הלקוחות — מי נכשל, מי עבר, החשבוניות מס שיצאו."*
   */
  readonly tenantUid: string;
  readonly tenantName: string;
  /** The provider's document number, or `null` when the attempt raised none. */
  readonly documentNumber: string | null;
  /**
   * `issued` · `pending_issue` · `provider_failed` · `void`.
   *
   * THE TWO STATES THE CUSTOMER'S OWN TAB NO LONGER SHOWS, in the place he asked for them:
   * *"משתמש הקצה לא צריך לראות טרם הופק או הפקה נכשלה — אלו דברים שמעניינים אותי כמנהל
   * פלטפורמה ולא כמשתמש קצה."*
   */
  readonly documentStatus: string;
  readonly periodStart: string;
  readonly periodEnd: string;
  readonly amountTotal: string;
  readonly currencyCode: string;
  readonly provider: string;
  readonly state: string;
  readonly providerTransactionId: string | null;
  readonly providerApproval: string | null;
  readonly alreadyExisted: boolean;
  readonly failureReason: string | null;
  readonly startedAtUtc: string;
  readonly settledAtUtc: string | null;
}

/**
 * THE OFFICE'S OWN CARD, AS THE OFFICE MAY SEE IT. 0041.
 *
 * ── WHAT IS ABSENT, AND WHY IT IS ABSENT STRUCTURALLY ──────────────────────────────────────────
 *
 * There is no token here. `billing.payment_instrument.provider_token` is a reference that can
 * CHARGE this office against our terminal, and the store's customer-facing reader does not select
 * the column at all — so it is not a field somebody remembered to leave out of this shape, it is a
 * value that never travels. The four `card*` fields are the last four digits, the brand and the
 * expiry, which is what a person needs to recognise their own card and is the most any product
 * should hold for that purpose.
 *
 * `tokenExpiresOn` IS THE PROVIDER'S, NOT THE CARD'S. Cardcom deletes a token on that date
 * (`TokenInfo.TokenExDate`), which is what lets the screen ask for a new card BEFORE a charge
 * fails rather than after. It is nullable because their field may be absent, and a token with no
 * known expiry is not a reason to refuse anything.
 */
export interface StoredCardView {
  readonly paymentInstrumentUid: string;
  /** `cardcom` | `sandbox`. A `sandbox` card is one no provider ever checked. */
  readonly provider: string;
  readonly tokenExpiresOn: string | null;
  readonly cardLast4: string | null;
  readonly cardBrand: string | null;
  readonly cardMonth: number | null;
  readonly cardYear: number | null;
}

/**
 * ONE CHARGE ATTEMPT, AS THE OFFICE AND THE PLATFORM BOTH READ IT. 0041.
 *
 * `state` IS FOUR WORDS AND THE FOURTH IS THE HONEST ONE. `in_flight` | `charged` | `declined` |
 * `unknown`. A decline is a settled fact and the dunning policy handles it on its own cadence;
 * `unknown` means the call to the provider did not complete, so the money MAY have moved and this
 * product does not know. Those are different situations and a screen that showed them as one word
 * would be hiding the only one that needs a person.
 *
 * `alreadyExisted` is TRUE when the provider answered 608 — *"that charge already exists"* — and
 * the original was read back. The period IS paid and this attempt did not pay it, which is worth
 * showing because it means something ran twice.
 */
export interface ChargeAttemptView {
  readonly chargeAttemptUid: string;
  readonly periodStart: string;
  readonly periodEnd: string;
  readonly amountTotal: string;
  readonly currencyCode: string;
  readonly provider: string;
  readonly state: string;
  readonly providerApproval: string | null;
  readonly alreadyExisted: boolean;
  readonly failureReason: string | null;
  readonly startedAtUtc: string;
  readonly settledAtUtc: string | null;
}

/**
 * ONE PLAN THIS OFFICE MAY MOVE ONTO, PRICED. **ITEM 4, 2026-11-03.**
 *
 * *"שדרוג/הורדה בשירות עצמי."* The published list, priced from the published book by the same
 * resolver the public door quotes with — so the price a customer was sold at and the price they
 * are offered now come from one implementation.
 *
 * It says NOTHING about whether the move will be allowed: §5.3's three rules and the
 * negotiated-terms refusal are asked by the ACT, against the state at that moment.
 */
export interface PlanOfferView {
  readonly planCode: string;
  readonly displayNameHe: string;
  /** The plan the office is on today. Listed and marked rather than hidden — both figures matter. */
  readonly current: boolean;
  readonly price: string;
  readonly priceDisplay: string | null;
  /** All three null together when no VAT rate is recorded. A real state, never a zero. */
  readonly vatRatePct: string | null;
  readonly vatDisplay: string | null;
  readonly totalDisplay: string | null;
  readonly currencyCode: string;
}

export interface PlanOffersView {
  readonly offers: readonly PlanOfferView[];
  /** `false` ⇒ no price book is published on this estate, which is a fault and not an empty list. */
  readonly priceBookAvailable: boolean;
}

/** One article a stranger may add at the door, priced from the published book. */
export interface PublicAddOnOfferView {
  readonly skuCode: string;
  readonly displayNameHe: string;
  /** What it opens, in the customer's words — `core.module.display_name_he`, never the code. */
  readonly moduleNamesHe: readonly string[];
  /** The working figure. `priceDisplay` is what a person reads. */
  readonly unitPrice: string;
  readonly priceDisplay: string | null;
  readonly currencyCode: string;
}

/**
 * THE THREE FIGURES FOR ONE COMBINATION — §9.6, *"הצג מחיר, מע״מ וסכום סופי לפני האישור"*.
 *
 * Computed on the server for the plan PLUS the chosen articles. A client that added the parts up
 * would be a second arithmetic about what somebody owes, on the screen where they agree to it.
 */
export interface SignupQuoteView {
  readonly planCode: string;
  readonly skuCodes: readonly string[];
  readonly price: string;
  readonly priceDisplay: string | null;
  readonly vatRatePct: string | null;
  readonly vatDisplay: string | null;
  readonly totalDisplay: string | null;
  readonly currencyCode: string;
  readonly periodKind: string;
  readonly trialDays: number;
  readonly firstChargeOn: string;
}

export interface PublicPlanOfferView {
  readonly planCode: string;
  readonly displayNameHe: string;
  /**
   * Fixed-point with four decimals, summed in integers on the server. Never a float.
   *
   * THE WORKING FIGURE, NOT THE ONE TO SHOW. `priceDisplay` is what a person reads.
   */
  readonly price: string;
  /** `100.00 ₪` — formatted once, server-side. §4: *"לא `390.0000 ILS`"*. */
  readonly priceDisplay: string | null;
  /** All three null together when no VAT rate is recorded for today. A real state, never a zero. */
  readonly vatRatePct: string | null;
  readonly vatDisplay: string | null;
  readonly totalDisplay: string | null;
  readonly currencyCode: string;
  readonly periodKind: string;
  readonly trialDays: number;
  /** The day the first charge falls if they register today. */
  readonly firstChargeOn: string;
  readonly priceBookUid: string;
  /**
   * THE ARTICLES THIS PLAN DOES NOT INCLUDE AND MAY BE EXTENDED WITH. **ITEM 1, 2026-11-03.**
   *
   * *"בחירת מודולים בהרשמה העצמית."* PER PLAN, because an article included in one plan is an
   * addition to another. EMPTY IS A REAL ANSWER on an estate whose modules are still `planned` —
   * the screen says so in words, because an absent section reads as "there is nothing to add".
   */
  readonly addOns: readonly PublicAddOnOfferView[];
}

/* ── 0044 · הוספת מודול ───────────────────────────────────────────────────────────────────────── */

/**
 * ONE ARTICLE THE OFFICE MAY ADD, AS THE SERVER DECIDED IT.
 *
 * EVERY JUDGEMENT ON THIS SHAPE IS THE SERVER'S. `purchasable` and `blockedBy` are read, not
 * recomputed: a browser that re-derived "may I buy this" from the price and the module statuses
 * would be a second copy of a rule that decides what a customer is charged for — and the two
 * copies would disagree the first time one changed. The panel renders the answer.
 *
 * `blockedBy` IS A CLOSED VOCABULARY — `not_for_sale` | `module_not_ready` | `unpriced` — and the
 * panel has a Hebrew sentence per member. A word outside it gets the uniform wording rather than
 * a blank, for the reason `TenantBillingScreen`'s refusal map gives.
 *
 * THE FIGURES: `unitPrice` is the price book's own decimal at four places, for anything that
 * computes; `unitPriceDisplay`, `vatDisplay` and `totalDisplay` are what a person READS, formatted
 * once on the server. The three display strings are null together when no VAT rate is in force —
 * a real state in which the article is still for sale and no total may be claimed.
 */
export interface ModuleOfferView {
  readonly skuCode: string;
  readonly displayNameHe: string;
  readonly moduleCodes: readonly string[];
  /** The Hebrew names of the modules included, from `core.module`. Never the codes. */
  readonly moduleNamesHe: readonly string[];
  readonly unitPrice: string | null;
  readonly currencyCode: string | null;
  readonly unitPriceDisplay: string | null;
  readonly vatRatePct: string | null;
  readonly vatDisplay: string | null;
  readonly totalDisplay: string | null;
  readonly purchasable: boolean;
  readonly blockedBy: string | null;
}

/**
 * THE TAB'S WHOLE READ: the articles, and the three facts that are about the OFFICE rather than
 * about any one article.
 *
 * `priceBookAvailable: false` is said ONCE at the top instead of `unpriced` beside every row —
 * nothing can be bought and no article is at fault.
 *
 * `paymentMethodOnFile: false` IS A WARNING AND NOT A GATE, deliberately. The purchase charges
 * nothing at the moment it is made: the line is priced into the next invoice by the billing run.
 * Refusing it for a missing card would be refusing the wrong act — and the office would be told
 * its card is the problem when it is not. The panel says a card will be needed before the invoice.
 *
 * `currentPeriodEndsOn` is the last day of the period the prorated share lands on, which is the
 * one fact that makes *"קונה מיד"* an honest sentence about money.
 */
export interface ModuleOffersView {
  readonly offers: readonly ModuleOfferView[];
  readonly priceBookAvailable: boolean;
  readonly paymentMethodOnFile: boolean;
  readonly currentPeriodEndsOn: string | null;
}

